import { Link } from "react-router-dom";
import {
  Info,
  ShieldCheck,
  FileText,
  HeartHandshake,
  BadgeCheck,
  MessagesSquare,
  Compass,
  ChevronRight,
  Users,
  TrendingUp,
  MessageSquare,
  Pin,
  Flame,
  Sparkles,
  Trophy,
  Dices,
  Spade,
  Rocket,
  Crown,
  Send,
  Bell,
  Heart,
  Eye,
  Clock,
  ArrowUpRight,
  Lock,
  Award,
  Zap,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Ban,
  Fingerprint,
  ShieldAlert,
  UserX,
} from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

/**
 * Shared layout for static info pages (About / Privacy / Terms / KYC /
 * Responsible Gaming / Forum / New Member Guide).
 * Renders a hero with an accent icon + title, an optional `lastUpdated`
 * line and the body in a readable column.
 */
const StaticPage = ({
  icon,
  title,
  subtitle,
  accent = "#19a55c",
  lastUpdated,
  testId,
  children,
}) => (
  <SiteLayout>
    <div
      className="relative overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${accent}22 0%, #0a1119 70%)` }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-14 flex items-center gap-3 sm:gap-4">
        <span
          className="h-11 w-11 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${accent}33`, color: accent }}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <h1
            data-testid={`${testId}-title`}
            className="text-2xl sm:text-5xl font-bold tracking-tight"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-white/60 mt-1.5 sm:mt-2 max-w-2xl text-sm sm:text-base">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>

    <div
      data-testid={`${testId}-body`}
      className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12"
    >
      {lastUpdated && (
        <p className="text-xs text-white/45 mb-6">Last updated: {lastUpdated}</p>
      )}
      <article className="prose-bj text-white/80 text-sm sm:text-base leading-relaxed space-y-5">
        {children}
      </article>

      <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-3 text-sm">
        <Link
          to="/contact"
          className="inline-flex items-center gap-1.5 text-[#19a55c] hover:text-[#21c46e] font-semibold"
        >
          Need help? Contact us <ChevronRight size={14} />
        </Link>
      </div>
    </div>
  </SiteLayout>
);

const H2 = ({ children }) => (
  <h2 className="text-xl sm:text-2xl font-bold text-white mt-8 mb-3 tracking-tight">
    {children}
  </h2>
);

/* ------------------------------------------------------------------ */
/* About Us — Premium brand storytelling redesign                     */
/* ------------------------------------------------------------------ */
const ABOUT_STATS = [
  { v: "10K+", l: "Active Players", icon: Users, color: "#22c55e" },
  { v: "1,000+", l: "Premium Games", icon: Dices, color: "#ec4899" },
  { v: "24/7", l: "Live Support", icon: HeartHandshake, color: "#06b6d4" },
  { v: "100%", l: "Welcome Bonus", icon: Sparkles, color: "#f59e0b" },
];

const ABOUT_PROVIDERS = [
  "Evolution",
  "Pragmatic Play",
  "JILI",
  "Microgaming",
  "Spribe",
  "Habanero",
  "PG Soft",
  "BetSoft",
];

const ABOUT_CHOOSE = [
  {
    icon: Sparkles,
    color: "#22c55e",
    title: "100% Welcome Bonus",
    desc: "Up to ৳20,000 on your first deposit — biggest in the region.",
  },
  {
    icon: Crown,
    color: "#f59e0b",
    title: "Daily Cashback & VIP",
    desc: "Tiered cashback, weekly reloads and exclusive VIP-only perks.",
  },
  {
    icon: MessagesSquare,
    color: "#06b6d4",
    title: "24/7 Bilingual Support",
    desc: "Live chat in English & Bangla — average response under 60s.",
  },
  {
    icon: Rocket,
    color: "#ec4899",
    title: "Lightning Withdrawals",
    desc: "Average payout: 15 minutes via bKash, Nagad, Rocket and bank.",
  },
  {
    icon: ShieldCheck,
    color: "#a855f7",
    title: "Licensed & Regulated",
    desc: "Anjouan eGaming licence ALSI-202410030-FI1 · GDPR-aligned.",
  },
  {
    icon: Spade,
    color: "#19a55c",
    title: "Mobile-First Design",
    desc: "Buttery-smooth Android app + responsive web — bet on the go.",
  },
];

const ABOUT_SPONSORS = [
  "Quetta Gladiators",
  "Sunrisers Eastern Cape",
  "Deccan Gladiators",
  "St Kitts & Nevis Patriots",
  "Biratnagar Kings",
];

export const AboutUsPage = () => (
  <SiteLayout>
    <div data-testid="about-page" className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
      {/* HERO */}
      <section
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/[0.07] mb-6 sm:mb-8"
        style={{
          background:
            "radial-gradient(120% 100% at 0% 0%, rgba(25,165,92,0.20), transparent 55%), radial-gradient(80% 60% at 100% 100%, rgba(245,158,11,0.10), transparent 60%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, #ffffff 1px, transparent 1.2px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-24 -right-12 h-80 w-80 rounded-full opacity-25 blur-3xl"
          style={{ background: "#19a55c" }}
        />
        <div
          aria-hidden
          className="absolute -bottom-24 -left-12 h-72 w-72 rounded-full opacity-15 blur-3xl"
          style={{ background: "#f59e0b" }}
        />

        <div className="relative p-6 sm:p-10 lg:p-14">
          <div className="flex items-center gap-2 mb-4 sm:mb-5">
            <span
              className="h-px w-10 sm:w-14"
              style={{ background: "linear-gradient(90deg, transparent, #19a55c)" }}
            />
            <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-black text-[#19a55c]">
              About Us · Est. 2023
            </span>
            <BadgeCheck size={12} className="text-[#19a55c]" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
            <div className="lg:col-span-7">
              <h1
                className="text-white font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Win like a{" "}
                <span
                  className="italic relative inline-block"
                  style={{
                    background:
                      "linear-gradient(135deg, #19a55c 0%, #22c55e 30%, #ffc933 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  king
                  <span
                    aria-hidden
                    className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, #19a55c 30%, #ffc933 70%, transparent)",
                    }}
                  />
                </span>
                .
              </h1>
              <p className="text-white/65 mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg max-w-2xl leading-relaxed">
                <span className="text-white font-semibold">Maxlive88</span> is
                South Asia's premier licensed online gaming destination — trusted
                by thousands of players across Bangladesh and beyond. We partner
                with world-class providers to bring you the most thrilling games
                in sports, live casino, slots, crash, table, fishing, arcade and
                lottery.
              </p>
              <div className="mt-6 sm:mt-7 flex flex-wrap items-center gap-2 sm:gap-2.5">
                {["Cricket", "Live Casino", "Slots", "Crash", "Lottery"].map(
                  (t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold border border-white/10 text-white/75 bg-white/[0.03] hover:border-[#19a55c]/40 hover:text-white transition-colors"
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ background: "#19a55c" }}
                      />
                      {t}
                    </span>
                  )
                )}
              </div>
            </div>

            <div className="lg:col-span-5 grid grid-cols-2 gap-2 sm:gap-3">
              {ABOUT_STATS.map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.l}
                    className="relative rounded-xl border border-white/[0.07] px-4 py-3.5 overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 60%), #0e1522",
                    }}
                  >
                    <div
                      aria-hidden
                      className="absolute -top-6 -right-6 h-20 w-20 rounded-full blur-2xl opacity-30"
                      style={{ background: s.color }}
                    />
                    <div className="relative flex items-center gap-3">
                      <div
                        className="shrink-0 h-10 w-10 rounded-lg flex items-center justify-center"
                        style={{
                          background: `${s.color}1f`,
                          boxShadow: `inset 0 0 0 1px ${s.color}55`,
                        }}
                      >
                        <Icon size={16} style={{ color: s.color }} />
                      </div>
                      <div className="min-w-0">
                        <div
                          className="text-white text-xl sm:text-2xl font-black leading-none"
                          style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                          }}
                        >
                          {s.v}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-bold mt-1">
                          {s.l}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION + STORY TWO COLUMN */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-6 sm:mb-10">
        <article
          className="lg:col-span-3 relative rounded-2xl border border-white/[0.07] p-6 sm:p-8 overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 100% at 100% 0%, rgba(25,165,92,0.10), transparent 55%), linear-gradient(180deg, #0d1421 0%, #0a111c 100%)",
          }}
        >
          <div
            aria-hidden
            className="absolute -top-12 -right-12 h-40 w-40 rounded-full blur-3xl opacity-25"
            style={{ background: "#19a55c" }}
          />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#19a55c] mb-2">
              ✦ Our mission
            </div>
            <h2
              className="text-white text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Premium gaming.{" "}
              <span className="italic text-[#19a55c]">Always fair.</span>
            </h2>
            <p className="text-white/70 text-sm sm:text-base leading-relaxed">
              To deliver a{" "}
              <span className="text-white font-semibold">
                premium, fair and entertaining
              </span>{" "}
              gaming experience to every player — backed by{" "}
              <span className="text-white font-semibold">24/7 support</span>,
              lightning withdrawals and industry-leading bonuses. We believe
              gaming should be transparent, secure and fun.
            </p>
            <div className="mt-5 sm:mt-6 grid grid-cols-3 gap-3 sm:gap-4">
              {[
                { v: "100%", l: "Provably Fair" },
                { v: "15 min", l: "Avg Payout" },
                { v: "<60s", l: "Chat Reply" },
              ].map((x) => (
                <div
                  key={x.l}
                  className="rounded-lg p-3 text-center border border-white/[0.07]"
                  style={{ background: "#0a0f1a" }}
                >
                  <div
                    className="text-lg sm:text-xl font-black leading-none"
                    style={{
                      background:
                        "linear-gradient(135deg, #19a55c 0%, #ffc933 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                      fontFamily: "'Playfair Display', Georgia, serif",
                    }}
                  >
                    {x.v}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-white/50 mt-1.5 font-bold">
                    {x.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article
          className="lg:col-span-2 relative rounded-2xl border border-white/[0.07] p-6 sm:p-7 overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 100% at 0% 100%, rgba(245,158,11,0.10), transparent 55%), linear-gradient(180deg, #0d1421 0%, #0a111c 100%)",
          }}
        >
          <div
            aria-hidden
            className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full blur-3xl opacity-25"
            style={{ background: "#f59e0b" }}
          />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#f59e0b] mb-2">
              ✦ Powered by
            </div>
            <h2 className="text-white text-xl sm:text-2xl font-black tracking-tight mb-4">
              World-class providers
            </h2>
            <div className="flex flex-wrap gap-2">
              {ABOUT_PROVIDERS.map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-white/10 text-white/85 bg-white/[0.03] hover:border-[#f59e0b]/40 transition-colors"
                >
                  <Sparkles size={11} className="text-[#f59e0b]" />
                  {p}
                </span>
              ))}
            </div>
          </div>
        </article>
      </div>

      {/* WHY CHOOSE — 6 BENEFITS GRID */}
      <section className="mb-6 sm:mb-10">
        <div className="mb-4 sm:mb-5 flex items-end justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#22c55e] mb-1">
              ✦ Player benefits
            </div>
            <h2
              className="text-white text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Why players choose{" "}
              <span className="italic text-[#19a55c]">Maxlive88</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {ABOUT_CHOOSE.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.title}
                data-testid={`about-benefit-${c.title.toLowerCase().replace(/\s/g,'-')}`}
                className="group relative rounded-2xl border border-white/[0.07] p-5 sm:p-6 overflow-hidden hover:border-white/20 transition-all"
                style={{
                  background:
                    "linear-gradient(180deg, #0d1421 0%, #0a111c 100%)",
                }}
              >
                <div
                  aria-hidden
                  className="absolute -top-12 -right-12 h-36 w-36 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                  style={{ background: c.color }}
                />
                <div className="relative">
                  <div
                    className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center mb-3 sm:mb-4"
                    style={{
                      background: `${c.color}1f`,
                      boxShadow: `inset 0 0 0 1px ${c.color}55`,
                    }}
                  >
                    <Icon size={18} style={{ color: c.color }} />
                  </div>
                  <h3 className="text-white font-bold text-base sm:text-lg leading-tight">
                    {c.title}
                  </h3>
                  <p className="text-white/55 text-sm mt-1.5 leading-relaxed">
                    {c.desc}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* SPONSORSHIPS RIBBON */}
      <section
        className="relative rounded-2xl sm:rounded-3xl border border-white/[0.07] p-6 sm:p-10 overflow-hidden mb-6 sm:mb-8"
        style={{
          background:
            "radial-gradient(120% 100% at 100% 0%, rgba(245,158,11,0.12), transparent 55%), radial-gradient(80% 60% at 0% 100%, rgba(25,165,92,0.10), transparent 60%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
        }}
      >
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <Trophy size={14} className="text-[#ffc933]" />
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-black text-[#ffc933]">
            Title sponsor of cricket's biggest leagues
          </span>
        </div>
        <h2
          className="text-white text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight mb-5 sm:mb-6"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Backing the{" "}
          <span
            className="italic"
            style={{
              background: "linear-gradient(135deg, #ffc933 0%, #ec4899 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            game we love
          </span>
          .
        </h2>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {ABOUT_SPONSORS.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold border border-white/10 text-white/90 hover:border-[#ffc933]/40 transition-colors"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,201,51,0.06), rgba(255,255,255,0)), #0d1421",
              }}
            >
              <Trophy size={12} className="text-[#ffc933]" />
              {s}
            </span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative rounded-2xl sm:rounded-3xl border border-white/[0.07] p-5 sm:p-7 overflow-hidden"
        style={{
          background:
            "radial-gradient(120% 100% at 100% 0%, rgba(25,165,92,0.14), transparent 55%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#19a55c] mb-2">
              ✦ Ready to play?
            </div>
            <h3 className="text-white text-lg sm:text-2xl font-black leading-tight mb-1.5">
              Join 10,000+ players today
            </h3>
            <p className="text-white/60 text-sm max-w-xl">
              Claim your 100% welcome bonus on first deposit — up to ৳20,000.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
            <Link
              to="/register"
              data-testid="about-register-cta"
              className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm transition-all hover:scale-[1.03]"
              style={{
                background: "linear-gradient(135deg, #19a55c 0%, #0f7a44 100%)",
                color: "#fff",
                boxShadow:
                  "0 10px 28px rgba(25,165,92,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
              }}
            >
              Sign up free <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm border border-white/15 text-white/85 hover:bg-white/5 transition-colors"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  </SiteLayout>
);

/* ------------------------------------------------------------------ */
/* Privacy Policy                                                     */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* Privacy Policy — Premium document layout                           */
/* ------------------------------------------------------------------ */
const PRIVACY_SECTIONS = [
  {
    id: "collect",
    icon: FileText,
    title: "Information we collect",
    color: "#06b6d4",
    items: [
      "Account details (username, full name, phone number, currency).",
      "Transactional data (deposits, withdrawals, bets and wins).",
      "Technical data (IP address, device information, browser type).",
      "KYC documents required for identity verification.",
    ],
  },
  {
    id: "use",
    icon: Compass,
    title: "How we use your information",
    color: "#22c55e",
    items: [
      "To operate and secure your account.",
      "To process deposits, withdrawals and bonus payouts.",
      "To comply with anti-money-laundering (AML) regulations.",
      "To send service updates, promotions and responsible-gaming notices (opt-out anytime).",
    ],
  },
  {
    id: "security",
    icon: Lock,
    title: "Data security",
    color: "#a855f7",
    body: (
      <p>
        We use industry-standard encryption{" "}
        <span className="text-white font-semibold">(TLS 1.3 in transit, AES-256 at rest)</span>{" "}
        and store sensitive data only as long as legally required. We{" "}
        <span className="text-white font-semibold">never sell</span> your personal
        data to third parties.
      </p>
    ),
  },
  {
    id: "rights",
    icon: BadgeCheck,
    title: "Your rights",
    color: "#f59e0b",
    body: (
      <p>
        You may request access, correction or deletion of your personal data at
        any time by emailing{" "}
        <a
          href="mailto:privacy@maxlive88.com"
          className="text-[#06b6d4] hover:text-[#22d3ee] underline decoration-dotted underline-offset-4 font-semibold"
        >
          privacy@maxlive88.com
        </a>
        . We respond within 30 days.
      </p>
    ),
  },
];

const PRIVACY_PILLARS = [
  { icon: Lock, label: "TLS 1.3 + AES-256", sub: "Encryption" },
  { icon: ShieldCheck, label: "GDPR Aligned", sub: "Compliance" },
  { icon: Eye, label: "Zero Selling", sub: "Data Policy" },
  { icon: Clock, label: "30-Day Response", sub: "Your Rights" },
];

export const PrivacyPolicyPage = () => (
  <SiteLayout>
    <div data-testid="privacy-page" className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
      {/* HERO */}
      <section
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/[0.07] mb-6 sm:mb-8"
        style={{
          background:
            "radial-gradient(120% 100% at 0% 0%, rgba(6,182,212,0.20), transparent 55%), radial-gradient(80% 60% at 100% 100%, rgba(168,85,247,0.10), transparent 60%), linear-gradient(135deg, #0b1622 0%, #07101c 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, #ffffff 1px, transparent 1.2px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-20 -right-10 h-72 w-72 rounded-full opacity-30 blur-3xl"
          style={{ background: "#06b6d4" }}
        />

        <div className="relative p-6 sm:p-10 lg:p-12">
          <div className="flex items-center gap-2 mb-4">
            <span
              className="h-px w-10"
              style={{ background: "linear-gradient(90deg, transparent, #06b6d4)" }}
            />
            <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] font-black text-[#06b6d4]">
              Legal · Updated January 2026
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                <div
                  className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(6,182,212,0.15)",
                    boxShadow: "inset 0 0 0 1px rgba(6,182,212,0.45)",
                  }}
                >
                  <ShieldCheck size={24} className="text-[#06b6d4]" />
                </div>
                <h1
                  className="text-white font-black tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[0.95]"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  Privacy{" "}
                  <span
                    className="italic"
                    style={{
                      background:
                        "linear-gradient(135deg, #06b6d4 0%, #22d3ee 50%, #a855f7 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Policy
                  </span>
                </h1>
              </div>
              <p className="text-white/65 text-sm sm:text-base lg:text-lg max-w-2xl leading-relaxed">
                Your privacy matters to us. This policy explains{" "}
                <span className="text-white font-semibold">what we collect</span>,{" "}
                <span className="text-white font-semibold">why we collect it</span>,{" "}
                and <span className="text-white font-semibold">how we keep it safe</span>.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-2 sm:gap-3">
              {PRIVACY_PILLARS.map((p) => {
                const Icon = p.icon;
                return (
                  <div
                    key={p.label}
                    className="relative rounded-xl border border-white/[0.07] px-3 py-3 sm:px-4 sm:py-3.5 overflow-hidden"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 60%), #0e1522",
                    }}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className="shrink-0 h-8 w-8 rounded-lg flex items-center justify-center"
                        style={{
                          background: "rgba(6,182,212,0.15)",
                          boxShadow: "inset 0 0 0 1px rgba(6,182,212,0.4)",
                        }}
                      >
                        <Icon size={14} className="text-[#06b6d4]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-white text-xs sm:text-sm font-bold leading-tight truncate">
                          {p.label}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-white/45 font-semibold mt-0.5">
                          {p.sub}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTIONS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {PRIVACY_SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <article
              key={s.id}
              data-testid={`privacy-section-${s.id}`}
              className="relative rounded-2xl border border-white/[0.07] p-5 sm:p-6 overflow-hidden hover:border-white/15 transition-colors"
              style={{
                background:
                  "linear-gradient(180deg, #0d1421 0%, #0a111c 100%)",
              }}
            >
              <div
                aria-hidden
                className="absolute -top-12 -right-12 h-36 w-36 rounded-full blur-3xl opacity-20"
                style={{ background: s.color }}
              />
              <div className="relative flex items-start gap-3 sm:gap-4 mb-4">
                <div
                  className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: `${s.color}1f`,
                    boxShadow: `inset 0 0 0 1px ${s.color}55`,
                  }}
                >
                  <Icon size={18} style={{ color: s.color }} />
                </div>
                <div className="min-w-0 pt-0.5">
                  <h2 className="text-white text-lg sm:text-xl font-black tracking-tight leading-tight">
                    {s.title}
                  </h2>
                </div>
              </div>
              <div className="relative text-white/70 text-sm sm:text-[15px] leading-relaxed">
                {s.body}
                {s.items && (
                  <ul className="space-y-2.5">
                    {s.items.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <CheckCircle2
                          size={14}
                          className="mt-1 shrink-0"
                          style={{ color: s.color }}
                        />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* CONTACT CTA */}
      <section
        className="relative mt-6 sm:mt-8 rounded-2xl sm:rounded-3xl border border-white/[0.07] p-5 sm:p-7 overflow-hidden"
        style={{
          background:
            "radial-gradient(120% 100% at 100% 0%, rgba(6,182,212,0.15), transparent 55%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.28em] font-black mb-2 text-[#06b6d4]">
              ✦ Data request
            </div>
            <h3 className="text-white text-lg sm:text-xl font-black leading-tight mb-1.5">
              Have a question about your data?
            </h3>
            <p className="text-white/60 text-sm max-w-xl">
              Our DPO responds to every privacy request within 30 days.
            </p>
          </div>
          <a
            href="mailto:privacy@maxlive88.com"
            data-testid="privacy-contact-cta"
            className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm shrink-0 transition-all hover:scale-[1.03]"
            style={{
              background: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
              color: "#fff",
              boxShadow:
                "0 10px 28px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}
          >
            Email DPO <ArrowUpRight size={16} />
          </a>
        </div>
      </section>
    </div>
  </SiteLayout>
);

/* ------------------------------------------------------------------ */
/* Terms & Conditions — Premium Document Layout                       */
/* ------------------------------------------------------------------ */

const TERMS_SECTIONS = [
  {
    id: "eligibility",
    n: "01",
    icon: BadgeCheck,
    color: "#19a55c",
    title: "Eligibility",
    summary: "Must be 18+ and legally allowed to gamble where you live.",
    body: (
      <>
        You must be at least <strong>18 years old</strong> (or the legal gaming age in your
        jurisdiction, whichever is higher) and legally permitted to gamble online
        in the country where you reside. Maxlive88 is not available in jurisdictions
        where online gambling is prohibited.
      </>
    ),
  },
  {
    id: "account",
    n: "02",
    icon: Lock,
    color: "#0ea5e9",
    title: "Account",
    summary: "One account per person, household, IP or device.",
    body: (
      <>
        You may only register and operate <strong>one account</strong>. Multiple accounts
        per person, household, IP or device are strictly prohibited. You are responsible
        for keeping your login credentials confidential — any activity carried out under
        your account is deemed to be authorised by you.
      </>
    ),
  },
  {
    id: "multi-account",
    n: "03",
    icon: ShieldAlert,
    color: "#ef4444",
    title: "একাধিক অ্যাকাউন্ট ও জালিয়াতি প্রতিরোধ নীতি",
    summary: "এক ব্যক্তি / এক IP / এক Device থেকে একাধিক অ্যাকাউন্ট — স্থায়ী সাসপেনশন।",
    body: (
      <div
        className="space-y-5 leading-[1.95] text-[15px] sm:text-[16px]"
        style={{ fontFamily: '"Hind Siliguri","Noto Sans Bengali","SolaimanLipi",system-ui,sans-serif' }}
      >
        {/* Top warning callout */}
        <div
          className="rounded-2xl border px-4 sm:px-5 py-4 sm:py-5 flex items-start gap-3 sm:gap-4"
          style={{
            background: "linear-gradient(135deg, rgba(239,68,68,0.10) 0%, rgba(239,68,68,0.04) 100%)",
            borderColor: "rgba(239,68,68,0.35)",
            boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.10)",
          }}
        >
          <span
            className="shrink-0 h-10 w-10 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(239,68,68,0.18)", color: "#ef4444", boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.45)" }}
          >
            <Ban size={20} />
          </span>
          <div>
            <div className="text-[12px] uppercase tracking-[0.22em] font-extrabold text-red-300/90 mb-1.5">
              Zero-Tolerance Policy
            </div>
            <p className="text-white/85 m-0">
              Maxlive88-এ <strong className="text-white">ন্যায্য খেলা (Fair Play)</strong> ও আর্থিক সুরক্ষা নিশ্চিত করতে আমরা নিচের নীতি কঠোরভাবে অনুসরণ করি। এই নিয়ম আপনাদের সকল প্রকৃত খেলোয়াড়কে বোনাস-অপব্যবহার, প্রতারণা ও অর্থ-পাচার (money laundering) থেকে রক্ষা করার জন্য।
            </p>
          </div>
        </div>

        {/* Rule 1 */}
        <div>
          <h4 className="text-white font-extrabold text-[16px] sm:text-[17px] mb-1.5 flex items-center gap-2">
            <span className="text-red-400">১.</span> এক ব্যক্তি — এক অ্যাকাউন্ট নীতি
          </h4>
          <p className="text-white/80 m-0">
            প্রত্যেক ব্যবহারকারী শুধুমাত্র <strong className="text-white">একটি (১টি)</strong> অ্যাকাউন্ট তৈরি ও পরিচালনা করতে পারবেন। নাম, NID/পাসপোর্ট, ফোন নম্বর, ই-মেইল, অথবা পেমেন্ট মাধ্যম (bKash / Nagad / Bank / USDT wallet) — এদের যে কোনো একটির মিল পাওয়া গেলে অ্যাকাউন্টগুলো একই ব্যক্তির হিসেবে গণ্য হবে।
          </p>
        </div>

        {/* Rule 2 */}
        <div>
          <h4 className="text-white font-extrabold text-[16px] sm:text-[17px] mb-1.5 flex items-center gap-2">
            <span className="text-red-400">২.</span> এক IP / এক Device নীতি
          </h4>
          <p className="text-white/80 m-0">
            একটি IP ঠিকানা, একটি Wi-Fi নেটওয়ার্ক, অথবা একটি Device (mobile / laptop / browser fingerprint) থেকে <strong className="text-white">একাধিক অ্যাকাউন্ট তৈরি বা পরিচালনা সম্পূর্ণ নিষিদ্ধ</strong>। পরিবারের একাধিক সদস্য পৃথক অ্যাকাউন্ট চালাতে চাইলে নিবন্ধনের পূর্বে অবশ্যই আমাদের Support টিমকে অবগত করতে হবে এবং প্রত্যেকের পৃথক KYC যাচাই সম্পন্ন করতে হবে।
          </p>
        </div>

        {/* Rule 3 — Detection */}
        <div>
          <h4 className="text-white font-extrabold text-[16px] sm:text-[17px] mb-1.5 flex items-center gap-2">
            <Fingerprint size={16} className="text-red-400" /> ৩. শনাক্তকরণ পদ্ধতি (Detection)
          </h4>
          <p className="text-white/80 mb-2">আমাদের Risk-Engine নিম্নলিখিত সংকেতগুলো রিয়েল-টাইমে পর্যবেক্ষণ করে:</p>
          <ul className="list-none pl-0 space-y-1.5 text-white/80">
            <li className="flex gap-2.5"><span className="text-red-400 mt-1">•</span> IP ঠিকানা, ASN, GeoLocation এবং VPN/Proxy চিহ্নিতকরণ</li>
            <li className="flex gap-2.5"><span className="text-red-400 mt-1">•</span> Device fingerprint — browser, OS, screen, timezone</li>
            <li className="flex gap-2.5"><span className="text-red-400 mt-1">•</span> KYC document, ফোন নম্বর, ই-মেইল ও পেমেন্ট-account এর duplication</li>
            <li className="flex gap-2.5"><span className="text-red-400 mt-1">•</span> Behavioural pattern — login সময়, betting pattern, chip-dumping ইত্যাদি</li>
          </ul>
        </div>

        {/* Rule 4 — Consequences (PROMINENT RED BOX) */}
        <div
          className="rounded-2xl border px-4 sm:px-5 py-4 sm:py-5"
          style={{
            background: "linear-gradient(135deg, rgba(239,68,68,0.14) 0%, rgba(127,29,29,0.08) 100%)",
            borderColor: "rgba(239,68,68,0.45)",
          }}
        >
          <h4 className="text-white font-extrabold text-[16px] sm:text-[17px] mb-2.5 flex items-center gap-2">
            <UserX size={18} className="text-red-400" /> ৪. লঙ্ঘনের পরিণতি (Consequences)
          </h4>
          <p className="text-white/85 mb-3 m-0">
            উপরোক্ত নিয়ম লঙ্ঘিত হলে — <strong className="text-red-300">পূর্ব-নোটিশ ব্যতিরেকে</strong> — নিম্নলিখিত পদক্ষেপ গ্রহণ করা হবে:
          </p>
          <ul className="list-none pl-0 space-y-2 text-white/90">
            <li className="flex gap-2.5">
              <XCircle size={16} className="text-red-400 mt-1 shrink-0" />
              <span>সংশ্লিষ্ট <strong className="text-white">সকল অ্যাকাউন্ট চিরদিনের জন্য স্থায়ীভাবে সাসপেন্ড</strong> (Permanent Suspension) করা হবে।</span>
            </li>
            <li className="flex gap-2.5">
              <XCircle size={16} className="text-red-400 mt-1 shrink-0" />
              <span>সমস্ত <strong className="text-white">বোনাস, প্রোমো-জয় ও জমাকৃত ব্যালেন্স বাজেয়াপ্ত</strong> (Forfeited) করা হবে।</span>
            </li>
            <li className="flex gap-2.5">
              <XCircle size={16} className="text-red-400 mt-1 shrink-0" />
              <span>লঙ্ঘিত অ্যাকাউন্ট থেকে কোনো প্রকার Withdrawal অনুরোধ গৃহীত হবে না।</span>
            </li>
            <li className="flex gap-2.5">
              <XCircle size={16} className="text-red-400 mt-1 shrink-0" />
              <span>প্রতারণার মাত্রা গুরুতর হলে — IP, NID ও পেমেন্ট-instrument <strong className="text-white">স্থায়ী Blacklist</strong>; প্রযোজ্য ক্ষেত্রে আইনি ব্যবস্থা গ্রহণ করা হবে।</span>
            </li>
          </ul>
        </div>

        {/* Rule 5 */}
        <div>
          <h4 className="text-white font-extrabold text-[16px] sm:text-[17px] mb-1.5 flex items-center gap-2">
            <span className="text-red-400">৫.</span> পুনরায় অ্যাকাউন্ট খোলা নিষিদ্ধ
          </h4>
          <p className="text-white/80 m-0">
            একবার স্থায়ী সাসপেনশন কার্যকর হলে, একই ব্যক্তি — নতুন নাম, নতুন NID, নতুন Device বা নতুন IP দিয়েও — Maxlive88-এ <strong className="text-white">পুনরায় অ্যাকাউন্ট তৈরি করতে পারবেন না</strong>। এমন অ্যাকাউন্ট ধরা পড়া মাত্রই অবিলম্বে নিষ্ক্রিয় করা হবে এবং সমস্ত fund বাজেয়াপ্ত হবে।
          </p>
        </div>

        {/* Closing commitment */}
        <div
          className="rounded-2xl border px-4 sm:px-5 py-4 flex items-start gap-3"
          style={{
            background: "linear-gradient(135deg, rgba(25,165,92,0.08) 0%, rgba(25,165,92,0.02) 100%)",
            borderColor: "rgba(25,165,92,0.28)",
          }}
        >
          <span
            className="shrink-0 h-9 w-9 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(25,165,92,0.18)", color: "#19a55c", boxShadow: "inset 0 0 0 1px rgba(25,165,92,0.4)" }}
          >
            <ShieldCheck size={18} />
          </span>
          <p className="text-white/85 m-0">
            <strong className="text-white">আমাদের অঙ্গীকার:</strong> এই নীতি কোনো প্রকৃত খেলোয়াড়কে সীমাবদ্ধ করার জন্য নয় — বরং আপনাদের সকলের সম্পদ ও আনন্দ সুরক্ষিত রাখার জন্য। <strong className="text-emerald-300">স্বচ্ছতা, ন্যায্যতা ও বিশ্বাস</strong> — এই তিন স্তম্ভের উপর Maxlive88 দাঁড়িয়ে আছে।
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "payments",
    n: "04",
    icon: ShieldCheck,
    color: "#a855f7",
    title: "Deposits & Withdrawals",
    summary: "All transactions final. Withdrawals may need KYC.",
    body: (
      <>
        All transactions are <strong>final once confirmed</strong>. Withdrawals may require
        KYC verification (ID + proof of address). Maxlive88 reserves the right to cancel
        bonuses and winnings obtained through fraudulent, abusive, or arbitrage behaviour,
        including bonus farming and chip-dumping.
      </>
    ),
  },
  {
    id: "bonuses",
    n: "05",
    icon: Award,
    color: "#ffc933",
    title: "Bonuses & Promotions",
    summary: "Wagering & timeframe rules apply to every bonus.",
    body: (
      <>
        Welcome and promotional bonuses are subject to <strong>wagering requirements</strong>
        and specific terms published on each promotion page. Failure to meet the wagering
        requirement within the stated timeframe voids the bonus and any winnings derived
        from it. Bonuses cannot be combined unless explicitly stated.
      </>
    ),
  },
  {
    id: "responsible",
    n: "06",
    icon: Heart,
    color: "#ef4444",
    title: "Responsible Gaming",
    summary: "Built-in tools — self-exclusion, deposit & time limits.",
    body: (
      <>
        Maxlive88 actively promotes <strong>responsible gaming</strong>. You can set
        deposit limits, loss limits, session time-outs and self-exclusion periods directly
        from your account settings. If gambling is no longer fun for you, please reach out
        to our 24/7 support team.
      </>
    ),
  },
  {
    id: "liability",
    n: "07",
    icon: AlertTriangle,
    color: "#f59e0b",
    title: "Limitation of Liability",
    summary: "Not liable for losses from technical failures beyond control.",
    body: (
      <>
        Maxlive88 is <strong>not liable</strong> for losses caused by technical failures
        beyond our reasonable control (ISP outages, force majeure, third-party game-provider
        issues), nor for losses resulting from your non-compliance with these Terms or
        applicable law.
      </>
    ),
  },
  {
    id: "changes",
    n: "08",
    icon: FileText,
    color: "#06b6d4",
    title: "Changes to Terms",
    summary: "We may update these terms — continued use = acceptance.",
    body: (
      <>
        We may update these Terms at any time without prior notice. Material changes will
        be highlighted on this page and announced via email when possible. <strong>Continued
        use</strong> of Maxlive88 after changes take effect constitutes acceptance of the
        revised Terms.
      </>
    ),
  },
];

const TERMS_HIGHLIGHTS = [
  { icon: BadgeCheck, color: "#19a55c", label: "Must be 18+" },
  { icon: Lock,       color: "#0ea5e9", label: "1 account per user" },
  { icon: ShieldCheck,color: "#a855f7", label: "KYC for withdrawal" },
  { icon: Award,      color: "#ffc933", label: "Bonus wagering applies" },
];

export const TermsPage = () => {
  const { settings } = useSiteSettings() || {};
  const s = settings || {};
  const heroSubtitle  = s.terms_hero_subtitle  || "By creating an account or placing a bet on Maxlive88 you agree to the rules below. We've kept them short, plain-language and skim-friendly.";
  const version       = s.terms_version        || "v3.2";
  const lastUpdated   = s.terms_last_updated   || "January 2026";
  const readMinutes   = s.terms_read_minutes   || "~5 min read";
  const ctaTitle      = s.terms_cta_title      || "Have a question about these Terms?";
  const ctaSubtitle   = s.terms_cta_subtitle   || "Our 24/7 support team is happy to clarify any clause in plain language.";
  const ctaButtonText = s.terms_cta_button_text|| "Contact support";
  const ctaButtonUrl  = s.terms_cta_button_url || "/contact";
  const footerNote    = s.terms_footer_note    || `Maxlive88 Terms ${version} · Effective ${lastUpdated}`;

  return (
  <SiteLayout>
    <div data-testid="terms-page" className="max-w-7xl lg:max-w-[1500px] mx-auto px-3 sm:px-6 py-5 sm:py-8">

      {/* ====================== HERO ====================== */}
      <section
        className="relative overflow-hidden rounded-3xl border border-white/[0.06] mb-6 sm:mb-9"
        style={{
          background:
            "radial-gradient(120% 100% at 0% 0%, rgba(255,201,51,0.16), transparent 55%), radial-gradient(120% 100% at 100% 100%, rgba(168,85,247,0.10), transparent 60%), linear-gradient(135deg, #0f1722 0%, #0a1119 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
        <div aria-hidden className="absolute -top-24 -right-16 h-[420px] w-[420px] rounded-full opacity-20 blur-3xl" style={{ background: "#ffc933" }} />
        <div aria-hidden className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full opacity-15 blur-3xl" style={{ background: "#a855f7" }} />

        <div className="relative px-5 sm:px-10 py-9 sm:py-14">
          <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10">
              <FileText size={12} className="text-[#ffc933]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-white/80">
                Legal · Last updated {lastUpdated}
              </span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: "rgba(255,201,51,0.08)", borderColor: "rgba(255,201,51,0.28)" }}>
              <span className="h-1.5 w-1.5 rounded-full bg-[#ffc933] shadow-[0_0_8px_#ffc933]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-[#ffc933]">
                Document No. MX-LEG-001 · {version}
              </span>
            </div>
          </div>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[0.95] max-w-4xl">
            Terms &{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, #ffc933 0%, #ff8a00 100%)" }}
            >
              Conditions
            </span>
          </h1>

          <p className="text-white/70 mt-4 sm:mt-5 text-sm sm:text-lg leading-relaxed max-w-3xl">
            {heroSubtitle}
          </p>
          <p
            className="text-white/55 mt-2 text-[13px] sm:text-[15px] leading-relaxed max-w-3xl"
            style={{ fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif' }}
          >
            Maxlive88-এ অ্যাকাউন্ট তৈরি বা বাজি ধরলেই আপনি নিচের শর্তাবলী মেনে নিচ্ছেন। আমরা সরল ভাষায় ও সংক্ষিপ্তভাবে লিখেছি — পড়তে মাত্র <strong className="text-white/80">৫ মিনিট</strong>।
          </p>

          {/* Meta strip */}
          <div className="mt-5 sm:mt-6 flex flex-wrap items-center gap-2.5 text-[12px] sm:text-[13px] text-white/65">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
              <Clock size={12} className="text-[#ffc933]" /> {readMinutes}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
              <CheckCircle2 size={12} className="text-[#19a55c]" /> 8 sections
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08]">
              <ShieldCheck size={12} className="text-[#0ea5e9]" /> {version}
            </span>
          </div>

          {/* Highlight strip */}
          <div className="mt-7 sm:mt-9 grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 max-w-3xl">
            {TERMS_HIGHLIGHTS.map((h) => (
              <div
                key={h.label}
                className="rounded-xl bg-white/[0.04] border border-white/[0.07] px-3 py-2.5 sm:px-4 sm:py-3 flex items-center gap-2.5"
              >
                <span
                  className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                  style={{ background: `${h.color}1c`, color: h.color, boxShadow: `inset 0 0 0 1px ${h.color}55` }}
                >
                  <h.icon size={13} />
                </span>
                <span className="text-[11px] sm:text-[12px] font-bold text-white/85 leading-tight">
                  {h.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== TWO-COLUMN LAYOUT ====================== */}
      <div className="grid lg:grid-cols-[260px_1fr] gap-6 lg:gap-10">

        {/* ========== Sticky TOC (sidebar) ========== */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45 mb-3 px-2">
              On this page
            </div>
            <nav className="space-y-0.5" data-testid="terms-toc">
              {TERMS_SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="group flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-semibold text-white/55 hover:text-white hover:bg-white/[0.04] transition-colors"
                  data-testid={`terms-toc-${s.id}`}
                >
                  <span
                    className="h-6 w-6 rounded-md flex items-center justify-center text-[10px] font-extrabold shrink-0 tabular-nums transition-colors"
                    style={{ background: `${s.color}1c`, color: s.color, boxShadow: `inset 0 0 0 1px ${s.color}40` }}
                  >
                    {s.n}
                  </span>
                  <span className="truncate">{s.title}</span>
                  <ChevronRight size={13} className="ml-auto opacity-0 group-hover:opacity-60 transition-opacity" />
                </a>
              ))}
            </nav>

            <div className="mt-6 rounded-xl border border-white/[0.06] bg-[#0f1722] p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#19a55c] mb-1.5">Need clarity?</div>
              <p className="text-[12px] text-white/60 leading-relaxed mb-3">
                Anything unclear? Our 24/7 support team is one click away.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#19a55c] hover:text-[#21c46e] transition-colors"
              >
                Contact support <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        </aside>

        {/* ========== Sections ========== */}
        <main className="min-w-0 space-y-3 sm:space-y-4">
          {TERMS_SECTIONS.map((s) => (
            <article
              key={s.id}
              id={s.id}
              data-testid={`terms-section-${s.id}`}
              className="group relative rounded-2xl border border-white/[0.06] bg-[#0f1722] overflow-hidden scroll-mt-24 transition-all duration-300 hover:border-white/[0.12] hover:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.5)] hover:-translate-y-0.5"
            >
              <div
                aria-hidden
                className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-15 blur-2xl"
                style={{ background: s.color }}
              />
              {/* Section header */}
              <header className="relative flex items-start gap-4 px-5 sm:px-7 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b border-white/[0.05]">
                <div
                  className="relative h-12 w-12 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${s.color}1c`, boxShadow: `inset 0 0 0 1px ${s.color}55` }}
                >
                  <s.icon size={22} style={{ color: s.color }} />
                  <span
                    className="absolute -top-1.5 -left-1.5 h-5 min-w-[22px] px-1 rounded-md text-[10px] font-extrabold tabular-nums flex items-center justify-center"
                    style={{ background: s.color, color: "#0a1119" }}
                  >
                    {s.n}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl font-extrabold text-white tracking-tight leading-tight">
                    {s.title}
                  </h2>
                  <p
                    className="text-[12px] sm:text-[13px] mt-1 font-semibold leading-relaxed"
                    style={{ color: `${s.color}cc` }}
                  >
                    {s.summary}
                  </p>
                </div>
                <a
                  href={`#${s.id}`}
                  aria-label="Anchor link"
                  className="hidden sm:inline-flex shrink-0 h-8 w-8 rounded-lg items-center justify-center text-white/30 hover:text-white/80 hover:bg-white/[0.05] transition-colors"
                >
                  <ChevronRight size={14} />
                </a>
              </header>

              {/* Section body */}
              <div className="relative px-5 sm:px-7 py-4 sm:py-5">
                <div className="text-white/70 text-[14px] sm:text-[15px] leading-relaxed">{s.body}</div>
              </div>
            </article>
          ))}

          {/* Final CTA card */}
          <section
            className="relative rounded-2xl border border-white/[0.06] overflow-hidden mt-2 sm:mt-4"
            style={{
              background:
                "radial-gradient(120% 100% at 100% 0%, rgba(25,165,92,0.14), transparent 55%), linear-gradient(135deg, #0f1722 0%, #0a1119 100%)",
            }}
          >
            <div aria-hidden className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#19a55c]/15 blur-3xl" />
            <div className="relative px-5 sm:px-7 py-6 sm:py-7 flex flex-col gap-4 sm:gap-5">
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-extrabold text-white tracking-tight leading-tight">
                  {ctaTitle}
                </h3>
                <p className="text-white/60 text-[13px] sm:text-sm mt-1 leading-relaxed">
                  {ctaSubtitle}
                </p>
              </div>
              <div className="flex flex-wrap gap-2.5">
                <Link
                  to={ctaButtonUrl}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#19a55c] hover:bg-[#15924f] text-white font-bold text-sm transition-all hover:gap-3"
                  data-testid="terms-contact-cta"
                >
                  {ctaButtonText} <ArrowUpRight size={15} />
                </Link>
                <Link
                  to="/privacy"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white font-semibold text-sm transition-colors"
                >
                  <ShieldCheck size={14} className="text-[#0ea5e9]" /> Privacy policy
                </Link>
                <Link
                  to="/responsible-gaming"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white font-semibold text-sm transition-colors"
                >
                  <Heart size={14} className="text-[#ef4444]" /> Responsible gaming
                </Link>
              </div>
            </div>
          </section>

          {/* Official Seal & Signature block */}
          <section
            className="relative rounded-2xl border overflow-hidden mt-4 sm:mt-5"
            style={{
              background:
                "radial-gradient(110% 100% at 0% 0%, rgba(255,201,51,0.10), transparent 60%), linear-gradient(135deg, #0c1320 0%, #0a1119 100%)",
              borderColor: "rgba(255,201,51,0.20)",
            }}
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,201,51,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,201,51,0.6) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
                maskImage: "radial-gradient(ellipse at center, black 25%, transparent 70%)",
              }}
            />
            <div className="relative px-5 sm:px-8 py-6 sm:py-8 grid sm:grid-cols-[1fr_auto] gap-6 items-center">
              {/* Left: Document attestation */}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.28em] font-extrabold text-[#ffc933] mb-2">
                  Official Document · প্রমাণিত নথি
                </div>
                <div
                  className="text-white font-extrabold text-lg sm:text-xl mb-1"
                  style={{ fontFamily: 'ui-serif, "Cormorant Garamond", "Times New Roman", serif', letterSpacing: "-0.01em" }}
                >
                  Maxlive88 Gaming Platform
                </div>
                <p className="text-white/55 text-[12px] sm:text-[13px] leading-relaxed max-w-md">
                  This document constitutes a binding legal agreement between you and Maxlive88. By using the platform you acknowledge that you have read, understood and agreed to every clause above.
                </p>
                <p
                  className="text-white/45 text-[11px] sm:text-[12px] mt-2 leading-relaxed max-w-md"
                  style={{ fontFamily: '"Hind Siliguri","Noto Sans Bengali",system-ui,sans-serif' }}
                >
                  এই নথিটি Maxlive88 ও ব্যবহারকারীর মধ্যে একটি বৈধ আইনি চুক্তি। প্ল্যাটফর্ম ব্যবহার করলেই আপনি প্রতিটি ধারা পড়েছেন ও মেনে নিয়েছেন বলে গণ্য হবে।
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] sm:text-[12px] text-white/55">
                  <span className="inline-flex items-center gap-1.5">
                    <FileText size={11} className="text-[#ffc933]" /> Doc No.{" "}
                    <span className="text-white/80 font-bold tabular-nums">MX-LEG-001</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={11} className="text-[#19a55c]" /> Version{" "}
                    <span className="text-white/80 font-bold tabular-nums">{version}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={11} className="text-[#0ea5e9]" /> Effective{" "}
                    <span className="text-white/80 font-bold">{lastUpdated}</span>
                  </span>
                </div>
              </div>

              {/* Right: Wax-seal style stamp */}
              <div className="flex sm:flex-col items-center sm:items-end gap-4 sm:gap-3 shrink-0">
                <div
                  className="relative h-24 w-24 sm:h-28 sm:w-28 rounded-full flex items-center justify-center select-none"
                  style={{
                    background:
                      "radial-gradient(circle at 35% 30%, #ffd76b 0%, #ffc933 35%, #c89220 75%, #8a6014 100%)",
                    boxShadow:
                      "0 8px 28px rgba(255,201,51,0.25), inset 0 0 0 2px rgba(255,255,255,0.18), inset 0 -10px 18px rgba(0,0,0,0.35)",
                  }}
                  aria-hidden="true"
                >
                  {/* Outer ring text */}
                  <div
                    className="absolute inset-1 rounded-full border-2 border-dashed flex items-center justify-center"
                    style={{ borderColor: "rgba(255,255,255,0.35)" }}
                  />
                  <div className="relative text-center leading-none">
                    <div
                      className="text-[8px] tracking-[0.28em] font-extrabold uppercase"
                      style={{ color: "#3a2806" }}
                    >
                      Maxlive88
                    </div>
                    <div
                      className="text-[9px] font-black mt-1"
                      style={{ color: "#3a2806", fontFamily: 'ui-serif, "Cormorant Garamond", serif' }}
                    >
                      ★ OFFICIAL ★
                    </div>
                    <div
                      className="text-[8px] tracking-[0.22em] font-extrabold uppercase mt-1"
                      style={{ color: "#3a2806" }}
                    >
                      Legal · {version}
                    </div>
                  </div>
                </div>
                <div className="text-right sm:text-right">
                  <div
                    className="text-white/85 text-base sm:text-lg italic"
                    style={{ fontFamily: '"Caveat","Dancing Script","Brush Script MT",cursive', letterSpacing: "0.02em" }}
                  >
                    Maxlive88 Legal
                  </div>
                  <div className="h-px w-32 sm:w-36 ml-auto mt-1 bg-gradient-to-r from-transparent via-[#ffc933]/60 to-transparent" />
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1.5 font-bold">
                    Authorised signatory
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Document footer */}
          <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-2 text-[11px] sm:text-[12px] text-white/40 px-2">
            <ShieldCheck size={11} className="text-[#19a55c]" />
            <span>{footerNote}</span>
            <span className="text-white/20">·</span>
            <span>For the latest version always refer to this page.</span>
          </div>
        </main>
      </div>
    </div>
  </SiteLayout>
  );
};

/* ------------------------------------------------------------------ */
/* Responsible Gaming                                                 */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* Responsible Gaming — Premium care-themed redesign                  */
/* ------------------------------------------------------------------ */
const RG_LIMITS = [
  { icon: Award, title: "Deposit Limits", sub: "Daily · Weekly · Monthly", color: "#ec4899" },
  { icon: Zap, title: "Wager Limits", sub: "Per gaming session", color: "#f59e0b" },
  { icon: Eye, title: "Loss Limits", sub: "Reality-check reminders", color: "#06b6d4" },
  { icon: Clock, title: "Session Time", sub: "Auto-logout reminders", color: "#a855f7" },
];

const RG_PAUSE_OPTIONS = [
  { d: "24h", t: "Cool-Off", note: "1-day break, reactivates automatically" },
  { d: "7d", t: "Short Break", note: "1-week pause for reset" },
  { d: "30d", t: "Time-Out", note: "1-month full account freeze" },
  { d: "∞", t: "Self-Exclude", note: "Permanent — irreversible" },
];

const RG_WARNING_SIGNS = [
  "Spending more time or money gaming than you planned.",
  "Chasing losses or gambling when upset.",
  "Lying to friends or family about your gaming activity.",
  "Borrowing money to gamble.",
  "Feeling guilty, anxious or depressed about gaming.",
  "Neglecting work, studies or relationships.",
];

const RG_HELPLINES = [
  {
    name: "GamCare",
    desc: "Free, confidential support 24/7",
    cta: "gamcare.org.uk",
    url: "https://www.gamcare.org.uk",
    color: "#22c55e",
  },
  {
    name: "BeGambleAware",
    desc: "Independent advice & treatment",
    cta: "begambleaware.org",
    url: "https://www.begambleaware.org",
    color: "#06b6d4",
  },
  {
    name: "Gamblers Anonymous",
    desc: "Peer support meetings worldwide",
    cta: "gamblersanonymous.org",
    url: "https://www.gamblersanonymous.org",
    color: "#a855f7",
  },
];

export const ResponsibleGamingPage = () => (
  <SiteLayout>
    <div data-testid="responsible-page" className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
      {/* HERO */}
      <section
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/[0.07] mb-6 sm:mb-8"
        style={{
          background:
            "radial-gradient(120% 100% at 0% 0%, rgba(236,72,153,0.20), transparent 55%), radial-gradient(80% 60% at 100% 100%, rgba(34,197,94,0.10), transparent 60%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, #ffffff 1px, transparent 1.2px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-24 -right-12 h-72 w-72 rounded-full opacity-25 blur-3xl"
          style={{ background: "#ec4899" }}
        />
        <div
          aria-hidden
          className="absolute -bottom-24 -left-12 h-64 w-64 rounded-full opacity-15 blur-3xl"
          style={{ background: "#22c55e" }}
        />

        <div className="relative p-6 sm:p-10 lg:p-14">
          <div className="flex items-center gap-2 mb-4 sm:mb-5">
            <Heart size={12} className="text-[#ec4899] fill-[#ec4899]" />
            <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-black text-[#ec4899]">
              Player Care · 24/7 Support
            </span>
            <span
              className="h-px w-10"
              style={{ background: "linear-gradient(90deg, #ec4899, transparent)" }}
            />
          </div>
          <h1
            className="text-white font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Play smart.{" "}
            <span
              className="italic block sm:inline"
              style={{
                background:
                  "linear-gradient(135deg, #ec4899 0%, #f472b6 50%, #f59e0b 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Stay in control.
            </span>
          </h1>
          <p className="text-white/65 mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg max-w-3xl leading-relaxed">
            Gaming should always be a form of entertainment — never a way to solve
            financial or emotional problems. Maxlive88 is committed to giving every
            player the tools they need to stay in control of their play.
          </p>
        </div>
      </section>

      {/* LIMIT TOOLS */}
      <section className="mb-6 sm:mb-10">
        <div className="flex items-end justify-between gap-3 mb-4 sm:mb-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#ec4899] mb-1">
              ✦ Tools at your fingertips
            </div>
            <h2 className="text-white text-2xl sm:text-3xl font-black tracking-tight">
              Set your limits
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {RG_LIMITS.map((l) => {
            const Icon = l.icon;
            return (
              <div
                key={l.title}
                data-testid={`rg-limit-${l.title.toLowerCase().replace(/\s/g,'-')}`}
                className="relative rounded-2xl border border-white/[0.07] p-4 sm:p-5 overflow-hidden hover:border-white/20 transition-all group"
                style={{
                  background:
                    "linear-gradient(180deg, #0d1421 0%, #0a111c 100%)",
                }}
              >
                <div
                  aria-hidden
                  className="absolute -top-10 -right-10 h-32 w-32 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                  style={{ background: l.color }}
                />
                <div className="relative">
                  <div
                    className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center mb-3"
                    style={{
                      background: `${l.color}1f`,
                      boxShadow: `inset 0 0 0 1px ${l.color}55`,
                    }}
                  >
                    <Icon size={18} style={{ color: l.color }} />
                  </div>
                  <h3 className="text-white font-bold text-base sm:text-lg leading-tight">
                    {l.title}
                  </h3>
                  <p className="text-white/50 text-xs sm:text-sm mt-1 leading-relaxed">
                    {l.sub}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* SELF-EXCLUSION TIMELINE */}
      <section className="mb-6 sm:mb-10">
        <div className="mb-4 sm:mb-5">
          <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#a855f7] mb-1">
            ✦ Take a break, anytime
          </div>
          <h2 className="text-white text-2xl sm:text-3xl font-black tracking-tight">
            Self-exclusion & time-outs
          </h2>
          <p className="text-white/55 text-sm mt-2 max-w-2xl">
            Pause your account instantly — choose the duration that works for you.
            Permanent self-exclusion is irreversible.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {RG_PAUSE_OPTIONS.map((p, i) => (
            <div
              key={p.d}
              className="relative rounded-2xl border border-white/[0.07] p-5 sm:p-6 overflow-hidden text-center hover:border-[#a855f7]/40 transition-colors"
              style={{
                background:
                  "linear-gradient(180deg, #0e1424 0%, #0a0f1a 100%)",
              }}
            >
              <div
                className="text-4xl sm:text-5xl font-black mb-2 leading-none"
                style={{
                  background:
                    i === 3
                      ? "linear-gradient(135deg,#ef4444,#a855f7)"
                      : "linear-gradient(135deg,#a855f7,#06b6d4)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  fontFamily: "'Playfair Display', Georgia, serif",
                }}
              >
                {p.d}
              </div>
              <div className="text-white font-bold text-sm sm:text-base">{p.t}</div>
              <div className="text-white/45 text-[11px] sm:text-xs mt-1 leading-relaxed">
                {p.note}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 sm:mt-5 rounded-2xl border border-white/[0.07] p-4 sm:p-5 flex flex-wrap items-center gap-3 sm:gap-4 bg-[#0e1424]/70">
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: "rgba(168,85,247,0.15)",
              boxShadow: "inset 0 0 0 1px rgba(168,85,247,0.4)",
            }}
          >
            <Send size={16} className="text-[#a855f7]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-semibold">Activate self-exclusion</div>
            <div className="text-white/55 text-xs sm:text-sm">
              Email{" "}
              <a
                href="mailto:responsible@maxlive88.com"
                className="text-[#a855f7] hover:text-[#c084fc] underline decoration-dotted underline-offset-4 font-semibold"
              >
                responsible@maxlive88.com
              </a>{" "}
              — we activate within 1 hour.
            </div>
          </div>
        </div>
      </section>

      {/* WARNING SIGNS + HELP TWO COLUMN */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Warning signs */}
        <article
          className="lg:col-span-3 relative rounded-2xl border border-white/[0.07] p-5 sm:p-7 overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 100% at 100% 0%, rgba(245,158,11,0.10), transparent 55%), linear-gradient(180deg, #110e08 0%, #07101c 100%)",
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center"
              style={{
                background: "rgba(245,158,11,0.15)",
                boxShadow: "inset 0 0 0 1px rgba(245,158,11,0.45)",
              }}
            >
              <AlertTriangle size={18} className="text-[#f59e0b]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-black text-[#f59e0b]">
                Watch out
              </div>
              <h2 className="text-white text-xl sm:text-2xl font-black tracking-tight">
                Warning signs
              </h2>
            </div>
          </div>
          <ul className="space-y-2.5 sm:space-y-3 text-white/75 text-sm sm:text-[15px]">
            {RG_WARNING_SIGNS.map((w, i) => (
              <li key={i} className="flex items-start gap-3">
                <XCircle
                  size={14}
                  className="mt-1 shrink-0 text-[#f59e0b]"
                />
                <span className="leading-relaxed">{w}</span>
              </li>
            ))}
          </ul>
        </article>

        {/* Helplines */}
        <aside className="lg:col-span-2 flex flex-col gap-3 sm:gap-4">
          <div className="rounded-2xl border border-white/[0.07] p-5 sm:p-6 bg-[#0d1421]">
            <div className="flex items-center gap-2.5 mb-2">
              <HeartHandshake size={16} className="text-[#22c55e]" />
              <span className="text-[10px] uppercase tracking-[0.28em] font-black text-[#22c55e]">
                Need to talk?
              </span>
            </div>
            <h3 className="text-white text-lg sm:text-xl font-black mb-2">
              Free help, 24/7
            </h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Confidential support is always available — no judgement, no cost.
            </p>
          </div>
          {RG_HELPLINES.map((h) => (
            <a
              key={h.name}
              href={h.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`helpline-${h.name.toLowerCase().replace(/\s/g,'-')}`}
              className="group relative rounded-xl border border-white/[0.07] p-4 hover:border-white/25 transition-colors flex items-center gap-3"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.02), rgba(255,255,255,0)), #0d1421",
              }}
            >
              <div
                className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                style={{
                  background: `${h.color}1f`,
                  boxShadow: `inset 0 0 0 1px ${h.color}55`,
                }}
              >
                <Heart size={14} style={{ color: h.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm">{h.name}</div>
                <div className="text-white/50 text-xs">{h.desc}</div>
                <div className="text-[11px] mt-0.5 font-mono" style={{ color: h.color }}>
                  {h.cta}
                </div>
              </div>
              <ArrowUpRight
                size={14}
                className="text-white/30 group-hover:text-white/80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
              />
            </a>
          ))}
        </aside>
      </div>

      {/* AGE & FAIR PLAY STRIP */}
      <section
        className="relative rounded-2xl sm:rounded-3xl border border-white/[0.07] p-5 sm:p-7 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(236,72,153,0.08) 0%, rgba(34,197,94,0.06) 100%), #0a0f1a",
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 items-center text-center sm:text-left">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center text-xl font-black shrink-0"
              style={{
                background: "rgba(239,68,68,0.15)",
                boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.45)",
                color: "#ef4444",
                fontFamily: "'Playfair Display', Georgia, serif",
              }}
            >
              18+
            </div>
            <div>
              <div className="text-white font-bold text-sm">Strictly 18+</div>
              <div className="text-white/55 text-xs leading-relaxed">
                Underage gaming is prohibited.
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: "rgba(34,197,94,0.15)",
                boxShadow: "inset 0 0 0 1px rgba(34,197,94,0.45)",
              }}
            >
              <ShieldCheck size={20} className="text-[#22c55e]" />
            </div>
            <div>
              <div className="text-white font-bold text-sm">Provably Fair</div>
              <div className="text-white/55 text-xs leading-relaxed">
                Independently audited RNG & RTP.
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: "rgba(6,182,212,0.15)",
                boxShadow: "inset 0 0 0 1px rgba(6,182,212,0.45)",
              }}
            >
              <Lock size={20} className="text-[#06b6d4]" />
            </div>
            <div>
              <div className="text-white font-bold text-sm">Bank-grade Privacy</div>
              <div className="text-white/55 text-xs leading-relaxed">
                AES-256 encrypted, GDPR-aligned.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </SiteLayout>
);

/* ------------------------------------------------------------------ */
/* KYC — full bespoke redesign                                         */
/* ------------------------------------------------------------------ */
export const KYCPage = () => {
  const accent = "#7c3aed";

  const docs = [
    {
      icon: BadgeCheck,
      title: "Proof of identity",
      hint: "Passport, driver's license, or National ID",
      detail: "Clear color photo of both sides — all four corners visible, no glare.",
      tone: "#7c3aed",
    },
    {
      icon: FileText,
      title: "Proof of address",
      hint: "Utility bill or bank statement",
      detail: "Issued within the last 3 months, showing your full name and address.",
      tone: "#0ea5e9",
    },
    {
      icon: ShieldCheck,
      title: "Selfie with ID",
      hint: "Hand-held verification photo",
      detail: "Holding your ID + a paper with today's date and your Maxlive88 username.",
      tone: "#19a55c",
    },
  ];

  const steps = [
    { n: "1", t: "Upload documents", d: "Submit ID, address proof and selfie via your profile." },
    { n: "2", t: "Compliance review", d: "Our team verifies authenticity within 24 hours." },
    { n: "3", t: "Account verified", d: "Withdrawals unlock + access exclusive VIP perks." },
  ];

  const safeguards = [
    { icon: ShieldCheck, t: "Bank-grade encryption", d: "Files encrypted at rest with AES-256." },
    { icon: BadgeCheck, t: "Compliance team only", d: "Access strictly limited and audit-logged." },
    { icon: FileText, t: "Auto-deletion", d: "Documents purged after the legal retention period." },
  ];

  const faq = [
    {
      q: "Why do I need to verify?",
      a: "AML and responsible-gaming regulations require Maxlive88 to verify every player before the first withdrawal. It also protects your account from fraud and unauthorized access.",
    },
    {
      q: "How long does it take?",
      a: "Most submissions are reviewed within 24 hours. You'll get an email + in-app notification the moment we approve your documents.",
    },
    {
      q: "What if my document is rejected?",
      a: "We'll tell you exactly which document needs to be re-submitted (usually a clearer photo). You can re-upload immediately — no penalty.",
    },
    {
      q: "Will my data be shared?",
      a: "Never. Documents are visible only to our internal compliance team, encrypted at rest, and deleted after the retention period required by law.",
    },
  ];

  return (
    <SiteLayout>
      <div data-testid="kyc-page" className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        {/* HERO */}
        <section
          className="relative overflow-hidden rounded-3xl border border-white/5 p-6 sm:p-12"
          style={{
            background:
              "radial-gradient(circle at 100% 0%, rgba(124,58,237,0.32) 0%, transparent 55%), radial-gradient(circle at 0% 100%, rgba(14,165,233,0.18) 0%, transparent 55%), linear-gradient(160deg, #14101f 0%, #0a1119 60%, #11141f 100%)",
          }}
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(135deg, #fff 0px, #fff 1px, transparent 1px, transparent 7px)",
            }}
          />
          <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12 items-center">
            <div>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold tracking-widest uppercase"
                style={{ background: `${accent}1f`, color: "#c8a9ff", border: `1px solid ${accent}55` }}
              >
                <BadgeCheck size={12} /> KYC Verification
              </span>
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-white mt-4 leading-[1.02]">
                Verify once.
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: `linear-gradient(135deg, #e9d5ff 0%, ${accent} 50%, #4c1d95 100%)` }}
                >
                  Withdraw forever.
                </span>
              </h1>
              <p className="text-white/65 mt-5 max-w-xl text-base sm:text-lg leading-relaxed">
                A one-time identity check unlocks every withdrawal — and protects your account
                from fraud. Most players are verified within{" "}
                <strong className="text-white">24 hours</strong>.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/profile/verification"
                  data-testid="kyc-cta-primary"
                  className="group h-12 px-6 rounded-xl font-bold text-sm sm:text-base inline-flex items-center gap-2 transition-all"
                  style={{
                    background: `linear-gradient(135deg, ${accent} 0%, #4c1d95 100%)`,
                    color: "#fff",
                    boxShadow: `0 10px 28px ${accent}55`,
                  }}
                >
                  Start verification
                  <ChevronRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="kyc-cta-help"
                  className="h-12 px-5 rounded-xl font-semibold text-sm sm:text-base inline-flex items-center gap-2 border border-white/15 text-white/85 hover:bg-white/5 transition-colors"
                >
                  Need help?
                  <ChevronRight size={15} />
                </Link>
              </div>
              <div className="mt-6 flex items-center gap-5 text-[12px] text-white/55">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-emerald-400" /> AES-256 encrypted
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <BadgeCheck size={14} className="text-emerald-400" /> 24h review
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Info size={14} className="text-emerald-400" /> Anjouan licensed
                </span>
              </div>
            </div>

            {/* Right: 3-step progress visual */}
            <div className="relative">
              <div
                className="rounded-2xl p-6 relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(160deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <div className="text-white/45 text-[11px] uppercase tracking-widest font-semibold mb-5">
                  Verification flow
                </div>
                <ol className="space-y-4">
                  {steps.map((s, i) => (
                    <li key={s.n} className="flex items-start gap-3">
                      <span
                        className="h-9 w-9 rounded-xl flex items-center justify-center font-extrabold text-sm shrink-0"
                        style={{
                          background:
                            i === 0
                              ? `linear-gradient(135deg, ${accent} 0%, #4c1d95 100%)`
                              : "rgba(255,255,255,0.05)",
                          color: i === 0 ? "#fff" : "rgba(255,255,255,0.55)",
                          border: i === 0 ? "none" : "1px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        {s.n}
                      </span>
                      <div className="pt-0.5">
                        <div className="text-white font-semibold text-sm">{s.t}</div>
                        <div className="text-white/50 text-[12px] mt-0.5 leading-relaxed">{s.d}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
              <div
                aria-hidden
                className="absolute -bottom-10 -right-10 h-44 w-44 rounded-full opacity-50 pointer-events-none blur-3xl"
                style={{ background: `radial-gradient(circle, ${accent} 0%, transparent 70%)` }}
              />
            </div>
          </div>
        </section>

        {/* DOCUMENT CHECKLIST */}
        <section className="mt-7 sm:mt-12">
          <div className="flex items-end justify-between mb-5 sm:mb-7">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#c8a9ff" }}>
                Documents you'll need
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1 tracking-tight">
                Three quick uploads. That's it.
              </h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {docs.map((d) => (
              <div
                key={d.title}
                data-testid={`kyc-doc-${d.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl p-5 border border-white/5 relative overflow-hidden h-full transition-all hover:border-white/15 hover:-translate-y-0.5"
                style={{
                  background:
                    "linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.25) 100%)",
                }}
              >
                <div
                  aria-hidden
                  className="absolute -top-6 -right-6 h-24 w-24 rounded-full opacity-25 blur-2xl"
                  style={{ background: `radial-gradient(circle, ${d.tone} 0%, transparent 70%)` }}
                />
                <div className="flex items-center gap-3 mb-3 relative">
                  <div
                    className="h-11 w-11 rounded-xl inline-flex items-center justify-center"
                    style={{ background: `${d.tone}1f`, color: d.tone, border: `1px solid ${d.tone}55` }}
                  >
                    <d.icon size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base leading-tight">{d.title}</h3>
                    <div className="text-white/45 text-[12px] mt-0.5">{d.hint}</div>
                  </div>
                </div>
                <p className="text-white/60 text-sm leading-relaxed relative">{d.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SECURITY / SAFEGUARDS */}
        <section
          className="mt-7 sm:mt-12 rounded-3xl p-6 sm:p-9 relative overflow-hidden border border-white/5"
          style={{
            background:
              "radial-gradient(circle at 100% 0%, rgba(25,165,92,0.18) 0%, transparent 50%), linear-gradient(135deg, #0a1119 0%, #0f1f17 100%)",
          }}
        >
          <div className="grid lg:grid-cols-[1fr_2fr] gap-6 sm:gap-10 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-300">
                Your data, locked down
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mt-1 tracking-tight">
                Privacy you can trust.
              </h3>
              <p className="text-white/55 text-sm sm:text-base mt-2 leading-relaxed">
                We hold ourselves to the same compliance bar as licensed banks.
                Your documents never leave our secure compliance vault.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
              {safeguards.map((s) => (
                <div
                  key={s.t}
                  className="rounded-xl p-4 border border-white/5"
                  style={{ background: "rgba(255,255,255,0.03)" }}
                >
                  <div className="h-9 w-9 rounded-lg inline-flex items-center justify-center bg-emerald-500/15 text-emerald-300 mb-2.5">
                    <s.icon size={17} />
                  </div>
                  <div className="text-white font-semibold text-sm">{s.t}</div>
                  <div className="text-white/50 text-[12px] mt-1 leading-relaxed">{s.d}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-7 sm:mt-12">
          <div className="text-[11px] uppercase tracking-widest font-bold mb-4 sm:mb-5" style={{ color: "#c8a9ff" }}>
            Frequently asked
          </div>
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
            {faq.map((f, i) => (
              <div
                key={i}
                data-testid={`kyc-faq-${i}`}
                className="rounded-2xl p-5 border border-white/5"
                style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.15) 100%)" }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 h-6 w-6 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: `${accent}20`, color: "#c8a9ff" }}
                  >
                    <Info size={14} />
                  </span>
                  <div>
                    <h4 className="text-white font-semibold text-sm sm:text-base">{f.q}</h4>
                    <p className="text-white/55 text-sm mt-1.5 leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section
          className="mt-7 sm:mt-12 rounded-3xl p-6 sm:p-9 relative overflow-hidden border border-white/5"
          style={{
            background:
              "radial-gradient(circle at 0% 50%, rgba(124,58,237,0.32) 0%, transparent 50%), linear-gradient(135deg, #0a1119 0%, #14101f 100%)",
          }}
        >
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-center relative">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Ready to verify your account?
              </h3>
              <p className="text-white/60 mt-2 text-sm sm:text-base max-w-2xl">
                Takes under 5 minutes to upload, and your wallet is unlocked within 24 hours.
                No fees, no hassle — just secure, hassle-free withdrawals from then on.
              </p>
            </div>
            <Link
              to="/profile/verification"
              data-testid="kyc-cta-bottom"
              className="h-12 px-6 rounded-xl font-bold text-sm sm:text-base inline-flex items-center gap-2 transition-all whitespace-nowrap"
              style={{
                background: "#fff",
                color: "#0a1119",
                boxShadow: "0 10px 28px rgba(255,255,255,0.18)",
              }}
            >
              Start verification now
              <ChevronRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
};

/* ------------------------------------------------------------------ */
/* BJ Forum — Premium Community Page                                  */
/* ------------------------------------------------------------------ */

const FORUM_CATEGORIES = [
  {
    id: "cricket",
    icon: Trophy,
    name: "Cricket Betting",
    desc: "IPL, BPL, PSL, T20 World Cup live tips & predictions.",
    color: "#19a55c",
    threads: "12.4K",
    posts: "184K",
    last: "‘BPL Final cover bet — anyone in?’",
    lastUser: "Rahim87",
    lastAt: "2m ago",
    pinned: true,
    hot: true,
  },
  {
    id: "slots",
    icon: Dices,
    name: "Slot Strategies",
    desc: "JILI, PG Soft, Pragmatic Play — bonus buy & RTP tips.",
    color: "#ef4444",
    threads: "8.7K",
    posts: "92K",
    last: "Sweet Bonanza 1000 hit — share screenshots",
    lastUser: "Tania_99",
    lastAt: "11m ago",
    hot: true,
  },
  {
    id: "casino",
    icon: Spade,
    name: "Live Casino",
    desc: "Roulette patterns, Baccarat road & Dragon Tiger.",
    color: "#a855f7",
    threads: "5.1K",
    posts: "61K",
    last: "Lightning Roulette — best multiplier streaks",
    lastUser: "AceShuvo",
    lastAt: "32m ago",
  },
  {
    id: "crash",
    icon: Rocket,
    name: "Aviator & Crash",
    desc: "Auto-cashout strategies, signals & community plays.",
    color: "#06b6d4",
    threads: "9.3K",
    posts: "104K",
    last: "100x finally landed at 02:14 AM 🚀",
    lastUser: "AviatorPro",
    lastAt: "1h ago",
    hot: true,
  },
  {
    id: "vip",
    icon: Crown,
    name: "VIP Club",
    desc: "Tier perks, exclusive events & personal manager Q&A.",
    color: "#ffc933",
    threads: "1.2K",
    posts: "14K",
    last: "Diamond tier weekend cashback boost",
    lastUser: "RoyalKamal",
    lastAt: "3h ago",
  },
  {
    id: "guides",
    icon: Compass,
    name: "Guides & Help",
    desc: "Deposit, withdrawal, KYC and bonus questions.",
    color: "#3b82f6",
    threads: "3.8K",
    posts: "28K",
    last: "Withdrawal under 5 minutes — bKash trick",
    lastUser: "Mod_Salim",
    lastAt: "4h ago",
  },
];

const TRENDING_THREADS = [
  { tag: "Cricket", color: "#19a55c", title: "BPL Final tomorrow — Rangpur vs Comilla, your live picks?", replies: 184, views: "12.4K", likes: 421, author: "RahimDhaka", avatar: "RD" },
  { tag: "Slots",   color: "#ef4444", title: "Gates of Olympus 1000 — anyone hit the 5000x lately?",         replies: 96,  views: "8.1K",  likes: 312, author: "SlotQueen",  avatar: "SQ" },
  { tag: "Crash",   color: "#06b6d4", title: "1.5x auto-cashout vs manual — share your daily P/L",            replies: 142, views: "9.6K",  likes: 287, author: "CrashKing", avatar: "CK" },
  { tag: "Casino",  color: "#a855f7", title: "Crazy Time — Coin Flip vs Cash Hunt expected value?",            replies: 58,  views: "5.2K",  likes: 198, author: "Maya_BD",   avatar: "MB" },
];

const FORUM_RULES = [
  { icon: Heart,        title: "Be respectful",    desc: "No personal attacks, slurs, or harassment of any kind.", tone: "ok" },
  { icon: Lock,         title: "Protect accounts", desc: "Never share login, OTP, or banking details — staff will never ask.", tone: "ok" },
  { icon: AlertTriangle,title: "No self-promotion",desc: "Do not post referral links, external sites or pirated content.", tone: "warn" },
  { icon: XCircle,      title: "Zero tolerance",   desc: "Threats, scams, or under-18 activity = permanent IP ban.", tone: "danger" },
];

const FORUM_HOSTS = [
  { name: "Rahim Hossain",   role: "Community Manager", avatar: "RH", color: "#19a55c", badge: "Staff" },
  { name: "Tania Akter",     role: "Slot Specialist",   avatar: "TA", color: "#ef4444", badge: "Mod"   },
  { name: "Salim Ahmed",     role: "Cricket Pundit",    avatar: "SA", color: "#3b82f6", badge: "Mod"   },
  { name: "Maya Chowdhury",  role: "Live Casino Host",  avatar: "MC", color: "#a855f7", badge: "Mod"   },
];

const ToneRing = { ok: "#19a55c", warn: "#f59e0b", danger: "#ef4444" };

export const ForumPage = () => {
  const { settings } = useSiteSettings() || {};
  const s = settings || {};
  const heroKicker        = s.forum_hero_kicker        || "Community · Live now";
  const heroTitle1        = s.forum_hero_title_1       || "Maxlive88";
  const heroTitle2        = s.forum_hero_title_2       || "Forum";
  const heroSubtitle      = s.forum_hero_subtitle      || "Connect with thousands of players. Share wins, swap strategies, and learn from the sharpest cricket-bettors, slot hunters and live-casino regulars in BD.";
  const joinCtaText       = s.forum_join_cta_text      || "Join the Community";
  const joinCtaUrl        = s.forum_join_cta_url       || "/register";
  const telegramUrl       = s.forum_telegram_url       || "https://t.me/";
  const whatsappUrl       = s.forum_whatsapp_url       || "https://wa.me/";
  const pulseLabel        = s.forum_pulse_label        || "Community Pulse";
  const statsMembers      = s.forum_stats_members_value|| "47.2K";
  const statsThreads      = s.forum_stats_threads_value|| "40.5K";
  const statsOnline       = s.forum_stats_online_value || "1,284";
  const pulseNote         = s.forum_pulse_note         || "Updated every 30 seconds · Asia/Dhaka";
  const launchKicker      = s.forum_launch_kicker      || "Launching soon · Q2 2026";
  const launchTitle       = s.forum_launch_title       || "The full community platform is coming.";
  const launchSubtitle    = s.forum_launch_subtitle    || "Until then, join our official Telegram & WhatsApp groups for live tips, IPL predictions, slot drops and instant support — moderated by the same hosts.";
  const launchTgText      = s.forum_launch_telegram_text || "Join Telegram";
  const launchWaText      = s.forum_launch_whatsapp_text || "WhatsApp";
  const launchNotifyText  = s.forum_launch_notify_text   || "Notify me on launch";

  if (s.forum_enabled === false) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-white/70">
          The community forum is currently unavailable. Please check back soon.
        </div>
      </SiteLayout>
    );
  }

  return (
  <SiteLayout>
    <div data-testid="forum-page" className="max-w-7xl lg:max-w-[1500px] mx-auto px-3 sm:px-6 py-5 sm:py-8">

      {/* ====================== HERO ====================== */}
      <section
        className="relative overflow-hidden rounded-3xl border border-white/[0.06] mb-6 sm:mb-9"
        style={{
          background:
            "radial-gradient(120% 100% at 0% 0%, rgba(14,165,233,0.18), transparent 55%), radial-gradient(120% 100% at 100% 100%, rgba(168,85,247,0.10), transparent 60%), linear-gradient(135deg, #0f1722 0%, #0a1119 100%)",
        }}
      >
        {/* grid pattern */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
        <div aria-hidden className="absolute -top-24 -right-16 h-72 w-72 sm:h-[420px] sm:w-[420px] rounded-full opacity-20 blur-3xl" style={{ background: "#0ea5e9" }} />
        <div aria-hidden className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full opacity-15 blur-3xl" style={{ background: "#a855f7" }} />

        <div className="relative px-5 sm:px-10 py-8 sm:py-14 grid lg:grid-cols-[1fr_360px] gap-7 lg:gap-12 items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 mb-4 sm:mb-5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inset-0 rounded-full bg-[#0ea5e9] animate-ping opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#0ea5e9]" />
              </span>
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-white/80">
                {heroKicker}
              </span>
            </div>

            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight text-white leading-[0.95]">
              {heroTitle1}{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(135deg, #0ea5e9 0%, #a855f7 100%)" }}
              >
                {heroTitle2}
              </span>
            </h1>

            <p className="text-white/70 mt-4 sm:mt-5 text-sm sm:text-lg leading-relaxed max-w-2xl">
              {heroSubtitle}
            </p>

            <div className="mt-6 sm:mt-7 flex flex-wrap gap-2.5">
              <Link
                to={joinCtaUrl}
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-xl bg-[#0ea5e9] text-white font-bold text-sm sm:text-base hover:bg-[#0284c7] transition-all hover:gap-3 hover:shadow-[0_10px_32px_-8px_rgba(14,165,233,0.6)] active:translate-y-px"
                data-testid="forum-join-cta"
              >
                {joinCtaText} <ArrowUpRight size={17} />
              </Link>
              <a
                href={telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-6 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white font-semibold text-sm sm:text-base transition-colors"
              >
                <Send size={16} className="text-[#0ea5e9]" /> Telegram channel
              </a>
            </div>
          </div>

          {/* Live stats card */}
          <div className="lg:w-[360px] shrink-0">
            <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#101a26] to-[#0a1119] p-5 sm:p-6 overflow-hidden">
              <div aria-hidden className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-[#0ea5e9]/20 blur-2xl" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold flex items-center gap-2">
                <Sparkles size={11} /> {pulseLabel}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[
                  { k: "Members", v: statsMembers, color: "#0ea5e9", icon: Users },
                  { k: "Threads", v: statsThreads, color: "#a855f7", icon: MessageSquare },
                  { k: "Online",  v: statsOnline,  color: "#19a55c", icon: TrendingUp, live: true },
                ].map((b) => (
                  <div key={b.k} className="rounded-xl bg-white/[0.04] border border-white/[0.06] py-3 px-2 text-center">
                    <b.icon size={14} className="mx-auto mb-1.5" style={{ color: b.color }} />
                    <div className="text-base sm:text-lg font-extrabold text-white tabular-nums">{b.v}</div>
                    <div className="text-[9px] uppercase tracking-wider text-white/55 mt-0.5 font-bold inline-flex items-center gap-1 justify-center">
                      {b.live && (
                        <span className="h-1 w-1 rounded-full bg-[#19a55c] animate-pulse" />
                      )}
                      {b.k}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 text-[11px] text-white/55">
                <Clock size={12} className="text-[#0ea5e9]" />
                {pulseNote}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====================== TRENDING NOW ====================== */}
      <section className="mb-7 sm:mb-9">
        <div className="flex items-end justify-between mb-4 gap-3">
          <div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-[#ef4444] mb-1.5 inline-flex items-center gap-1.5">
              <Flame size={11} /> Trending now
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Hot threads this hour
            </h2>
          </div>
          <Link
            to="/forum"
            className="text-[12px] sm:text-sm font-semibold text-white/65 hover:text-white inline-flex items-center gap-1 transition-colors"
          >
            View all <ChevronRight size={14} />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {TRENDING_THREADS.map((t, i) => (
            <article
              key={i}
              className="group relative rounded-2xl border border-white/[0.06] bg-[#0f1722] p-4 sm:p-5 hover:border-white/15 hover:bg-[#121b29] transition-all overflow-hidden"
              data-testid={`forum-trending-${i}`}
            >
              <div
                aria-hidden
                className="absolute -top-12 -right-12 h-28 w-28 rounded-full opacity-15 blur-2xl"
                style={{ background: t.color }}
              />
              <div className="relative flex items-start gap-3">
                <div
                  className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center shrink-0 font-extrabold text-sm tracking-wide"
                  style={{
                    background: `${t.color}1c`,
                    color: t.color,
                    boxShadow: `inset 0 0 0 1px ${t.color}40`,
                  }}
                >
                  {t.avatar}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider"
                      style={{ background: `${t.color}22`, color: t.color, boxShadow: `inset 0 0 0 1px ${t.color}40` }}
                    >
                      {t.tag}
                    </span>
                    <span className="text-[11px] text-white/45 font-medium">by</span>
                    <span className="text-[12px] text-white/80 font-semibold truncate">{t.author}</span>
                  </div>
                  <h3 className="text-white font-bold text-[14px] sm:text-[15px] leading-snug line-clamp-2 group-hover:text-white">
                    {t.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-4 text-[11px] sm:text-[12px] text-white/55 font-semibold">
                    <span className="inline-flex items-center gap-1"><MessageSquare size={11} /> {t.replies}</span>
                    <span className="inline-flex items-center gap-1"><Eye size={11} /> {t.views}</span>
                    <span className="inline-flex items-center gap-1"><Heart size={11} /> {t.likes}</span>
                    <span className="ml-auto text-white/35 inline-flex items-center gap-1"><Clock size={10} /> live</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ====================== TOP CATEGORIES ====================== */}
      <section className="mb-7 sm:mb-9">
        <div className="flex items-end justify-between mb-4 gap-3">
          <div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-[#0ea5e9] mb-1.5 inline-flex items-center gap-1.5">
              <Zap size={11} /> Browse by topic
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Top categories
            </h2>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {FORUM_CATEGORIES.map((c) => (
            <article
              key={c.id}
              className="group relative rounded-2xl border border-white/[0.06] bg-[#0f1722] p-5 hover:border-white/15 hover:-translate-y-0.5 transition-all overflow-hidden"
              data-testid={`forum-cat-${c.id}`}
            >
              <div aria-hidden className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-15 blur-2xl" style={{ background: c.color }} />
              <div className="relative flex items-start justify-between gap-3 mb-3">
                <div
                  className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${c.color}1c`, boxShadow: `inset 0 0 0 1px ${c.color}45` }}
                >
                  <c.icon size={22} style={{ color: c.color }} />
                </div>
                <div className="flex items-center gap-1.5">
                  {c.pinned && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-1 rounded bg-[#0ea5e9]/15 text-[#0ea5e9]" style={{ boxShadow: "inset 0 0 0 1px rgba(14,165,233,0.4)" }}>
                      <Pin size={9} /> Pinned
                    </span>
                  )}
                  {c.hot && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-1 rounded bg-[#ef4444]/15 text-[#ef4444]" style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.4)" }}>
                      <Flame size={9} /> Hot
                    </span>
                  )}
                </div>
              </div>

              <h3 className="text-white font-bold text-[15px] sm:text-base leading-tight">{c.name}</h3>
              <p className="text-white/55 text-[12px] sm:text-[13px] mt-1 leading-relaxed">{c.desc}</p>

              <div className="mt-3.5 flex items-center gap-3 text-[11px] sm:text-[12px] text-white/55 font-semibold">
                <span className="inline-flex items-center gap-1"><MessageSquare size={11} /> {c.threads} threads</span>
                <span className="text-white/20">·</span>
                <span className="tabular-nums">{c.posts} posts</span>
              </div>

              <div className="mt-3 pt-3 border-t border-white/[0.05]">
                <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-1">Latest reply</div>
                <div className="text-[12px] sm:text-[13px] text-white/80 truncate">{c.last}</div>
                <div className="text-[11px] text-white/45 mt-0.5">
                  <span className="text-white/65 font-semibold">{c.lastUser}</span> · {c.lastAt}
                </div>
              </div>

              <span
                aria-hidden
                className="absolute right-4 bottom-4 opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0 transition-all"
                style={{ color: c.color }}
              >
                <ArrowUpRight size={16} />
              </span>
            </article>
          ))}
        </div>
      </section>

      {/* ====================== HOSTS ====================== */}
      <section className="mb-7 sm:mb-9">
        <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#0f1722] to-[#0a1119] p-5 sm:p-7 overflow-hidden relative">
          <div aria-hidden className="absolute -top-16 -left-16 h-48 w-48 rounded-full bg-[#0ea5e9]/10 blur-3xl" />
          <div className="relative grid sm:grid-cols-[1fr_auto] items-center gap-5 mb-5">
            <div>
              <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-[#ffc933] mb-1.5 inline-flex items-center gap-1.5">
                <Award size={11} /> Verified hosts
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                Meet your moderators
              </h2>
              <p className="text-white/55 text-sm mt-1">
                Trained Maxlive88 staff & power-users keeping the conversation clean and helpful.
              </p>
            </div>
          </div>

          <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3">
            {FORUM_HOSTS.map((h) => (
              <div
                key={h.name}
                className="rounded-2xl border border-white/[0.06] bg-[#0f1722] p-4 text-center hover:border-white/15 transition-colors"
              >
                <div
                  className="h-14 w-14 mx-auto rounded-full flex items-center justify-center font-extrabold text-base mb-2.5"
                  style={{
                    background: `${h.color}1c`,
                    color: h.color,
                    boxShadow: `inset 0 0 0 2px ${h.color}50`,
                  }}
                >
                  {h.avatar}
                </div>
                <div className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/[0.05] text-white/70 mb-1.5" style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)" }}>
                  <BadgeCheck size={9} className="text-[#0ea5e9]" /> {h.badge}
                </div>
                <div className="text-white font-bold text-[13px] truncate">{h.name}</div>
                <div className="text-white/50 text-[11px] truncate">{h.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== RULES ====================== */}
      <section className="mb-7 sm:mb-9">
        <div className="flex items-end justify-between mb-4 gap-3">
          <div>
            <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-[#19a55c] mb-1.5 inline-flex items-center gap-1.5">
              <ShieldCheck size={11} /> House rules
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Forum guidelines
            </h2>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {FORUM_RULES.map((r) => {
            const ring = ToneRing[r.tone];
            return (
              <div
                key={r.title}
                className="relative rounded-2xl border border-white/[0.06] bg-[#0f1722] p-5 overflow-hidden"
              >
                <div
                  aria-hidden
                  className="absolute -top-10 -right-10 h-24 w-24 rounded-full opacity-15 blur-2xl"
                  style={{ background: ring }}
                />
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: `${ring}1c`, boxShadow: `inset 0 0 0 1px ${ring}55`, color: ring }}
                >
                  <r.icon size={18} />
                </div>
                <h3 className="text-white font-bold text-[14px] sm:text-[15px]">{r.title}</h3>
                <p className="text-white/55 text-[12px] sm:text-[13px] mt-1 leading-relaxed">{r.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ====================== LAUNCH BANNER ====================== */}
      <section
        className="rounded-3xl border border-white/[0.06] overflow-hidden relative"
        style={{
          background:
            "radial-gradient(120% 100% at 100% 0%, rgba(168,85,247,0.18), transparent 55%), radial-gradient(120% 100% at 0% 100%, rgba(14,165,233,0.14), transparent 60%), linear-gradient(135deg, #0f1722 0%, #0a1119 100%)",
        }}
      >
        <div aria-hidden className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#a855f7]/15 blur-3xl" />
        <div aria-hidden className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-[#0ea5e9]/10 blur-3xl" />

        <div className="relative px-5 sm:px-9 py-7 sm:py-10 grid lg:grid-cols-[1fr_auto] gap-6 lg:gap-10 items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 mb-3">
              <Sparkles size={12} className="text-[#a855f7]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold text-white/80">
                {launchKicker}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight">
              {launchTitle}
            </h2>
            <p className="text-white/65 text-sm sm:text-base mt-2 leading-relaxed max-w-2xl">
              {launchSubtitle}
            </p>
          </div>

          <div className="flex flex-wrap gap-2.5">
            <a
              href={telegramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 rounded-xl bg-[#0ea5e9] text-white font-bold text-sm hover:bg-[#0284c7] transition-all hover:gap-3"
              data-testid="forum-telegram-cta"
            >
              <Send size={16} /> {launchTgText}
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 rounded-xl bg-[#19a55c] text-white font-bold text-sm hover:bg-[#15924f] transition-all hover:gap-3"
              data-testid="forum-whatsapp-cta"
            >
              <MessagesSquare size={16} /> {launchWaText}
            </a>
            <button
              type="button"
              className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white font-semibold text-sm transition-colors"
              data-testid="forum-notify-cta"
            >
              <Bell size={15} /> {launchNotifyText}
            </button>
          </div>
        </div>
      </section>

      {/* Contact link */}
      <div className="mt-8 pt-6 border-t border-white/[0.06] flex flex-wrap gap-3 text-sm">
        <Link
          to="/contact"
          className="inline-flex items-center gap-1.5 text-[#0ea5e9] hover:text-[#38bdf8] font-semibold"
        >
          Need help? Contact us <ChevronRight size={14} />
        </Link>
      </div>
    </div>
  </SiteLayout>
  );
};

/* ------------------------------------------------------------------ */
/* New Member Guide                                                   */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* New Member Guide — Premium step-by-step journey                    */
/* ------------------------------------------------------------------ */
const NMG_STEPS = [
  {
    n: "01",
    icon: Users,
    t: "Register",
    d: "Sign up with a username, password and phone number — takes 30 seconds.",
    color: "#22c55e",
    time: "30s",
  },
  {
    n: "02",
    icon: ShieldCheck,
    t: "Verify your phone",
    d: "We'll send a one-time code to confirm your number. SMS arrives in seconds.",
    color: "#06b6d4",
    time: "10s",
  },
  {
    n: "03",
    icon: Award,
    t: "Make first deposit",
    d: "Choose from bKash, Nagad, Rocket or local bank transfer. Minimum ৳100.",
    color: "#f59e0b",
    time: "1 min",
  },
  {
    n: "04",
    icon: Sparkles,
    t: "Claim welcome bonus",
    d: "Activate your 100% welcome bonus (up to ৳20,000) on your first deposit.",
    color: "#ec4899",
    time: "1 tap",
  },
  {
    n: "05",
    icon: Dices,
    t: "Pick your game",
    d: "Browse Sports, Live Casino, Slots, Crash, Table, Fishing, Arcade & Lottery.",
    color: "#a855f7",
    time: "Instant",
  },
  {
    n: "06",
    icon: Trophy,
    t: "Withdraw winnings",
    d: "Complete KYC once — future withdrawals are one-tap. Avg payout: 15 minutes.",
    color: "#19a55c",
    time: "15 min",
  },
];

const NMG_PAYMENTS = [
  { name: "bKash", color: "#e2136e" },
  { name: "Nagad", color: "#f57c00" },
  { name: "Rocket", color: "#8b1d8b" },
  { name: "Bank", color: "#06b6d4" },
  { name: "USDT", color: "#22c55e" },
];

const NMG_STATS = [
  { v: "5 min", l: "Total Setup" },
  { v: "100%", l: "Welcome Bonus" },
  { v: "৳20K", l: "Max Bonus" },
  { v: "24/7", l: "Live Support" },
];

const NMG_FAQS = [
  {
    q: "Is Maxlive88 legal in Bangladesh?",
    a: "Maxlive88 operates under an Anjouan eGaming license (ALSI-202410030-FI1). We follow international gaming laws. Players must be 18+ and verify they are not in a restricted jurisdiction.",
  },
  {
    q: "How fast are withdrawals?",
    a: "Average payout is 15 minutes via bKash, Nagad or Rocket once KYC is approved. Bank transfers process in 1–3 hours during business hours.",
  },
  {
    q: "Do I have to deposit to play?",
    a: "Most games are real-money only, but slots and crash titles offer demo modes after registration — perfect for trying before betting.",
  },
  {
    q: "What if I forget my password?",
    a: "Tap 'Forgot password' on the login screen — we'll send a reset link to your verified phone within seconds.",
  },
];

export const NewMemberGuidePage = () => (
  <SiteLayout>
    <style>{`
      @keyframes nmgPulse {
        0%,100% { transform: scale(1); opacity: 0.6; }
        50% { transform: scale(1.15); opacity: 1; }
      }
    `}</style>
    <div data-testid="guide-page" className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
      {/* HERO */}
      <section
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-white/[0.07] mb-6 sm:mb-8"
        style={{
          background:
            "radial-gradient(120% 100% at 0% 0%, rgba(34,197,94,0.20), transparent 55%), radial-gradient(80% 60% at 100% 100%, rgba(6,182,212,0.10), transparent 60%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, #ffffff 1px, transparent 1.2px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-20 -right-10 h-72 w-72 rounded-full opacity-25 blur-3xl"
          style={{ background: "#22c55e" }}
        />

        <div className="relative p-6 sm:p-10 lg:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 mb-4">
                <span
                  className="inline-flex h-2 w-2 rounded-full"
                  style={{
                    background: "#22c55e",
                    animation: "nmgPulse 2s ease-in-out infinite",
                  }}
                />
                <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-black text-[#22c55e]">
                  Onboarding · Live walk-through
                </span>
              </div>
              <h1
                className="text-white font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                Start{" "}
                <span
                  className="italic"
                  style={{
                    background:
                      "linear-gradient(135deg, #22c55e 0%, #06b6d4 50%, #f59e0b 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  winning
                </span>
                <br />
                in 5 minutes.
              </h1>
              <p className="text-white/65 mt-4 sm:mt-5 text-sm sm:text-base lg:text-lg max-w-2xl leading-relaxed">
                Welcome to <span className="text-white font-semibold">Maxlive88</span>.
                Six simple steps to register, claim your welcome bonus and start
                playing across 1,000+ games.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-2.5 sm:gap-3">
                <Link
                  to="/register"
                  data-testid="guide-register-cta"
                  className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm transition-all hover:scale-[1.03]"
                  style={{
                    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                    color: "#fff",
                    boxShadow:
                      "0 10px 28px rgba(34,197,94,0.40), inset 0 1px 0 rgba(255,255,255,0.2)",
                  }}
                >
                  Sign up free <ChevronRight size={16} />
                </Link>
                <Link
                  to="/promotions"
                  className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm border border-white/15 text-white/85 hover:bg-white/5 transition-colors"
                >
                  See bonuses
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-2 sm:gap-3">
              {NMG_STATS.map((s, i) => (
                <div
                  key={s.l}
                  className="relative rounded-xl border border-white/[0.07] px-4 py-3.5 overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 60%), #0e1522",
                  }}
                >
                  <div
                    aria-hidden
                    className="absolute -top-6 -right-6 h-20 w-20 rounded-full blur-2xl opacity-25"
                    style={{
                      background: ["#22c55e", "#ec4899", "#f59e0b", "#06b6d4"][i],
                    }}
                  />
                  <div className="relative">
                    <div
                      className="text-2xl sm:text-3xl font-black leading-none"
                      style={{
                        background:
                          i % 2 === 0
                            ? "linear-gradient(135deg,#22c55e,#06b6d4)"
                            : "linear-gradient(135deg,#ec4899,#f59e0b)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                        fontFamily: "'Playfair Display', Georgia, serif",
                      }}
                    >
                      {s.v}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold mt-1.5">
                      {s.l}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STEP TIMELINE */}
      <section className="mb-6 sm:mb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {NMG_STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.n}
                data-testid={`guide-step-${s.n}`}
                className="group relative rounded-2xl border border-white/[0.07] p-5 sm:p-6 overflow-hidden hover:border-white/20 transition-all"
                style={{
                  background:
                    "linear-gradient(180deg, #0d1421 0%, #0a111c 100%)",
                  animation: `ambassadorRise 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${i * 70}ms backwards`,
                }}
              >
                <div
                  aria-hidden
                  className="absolute -top-12 -right-12 h-36 w-36 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
                  style={{ background: s.color }}
                />
                {/* Number ribbon */}
                <div className="relative flex items-start justify-between mb-4">
                  <div
                    className="text-5xl sm:text-6xl font-black leading-none tracking-tight"
                    style={{
                      background: `linear-gradient(135deg, ${s.color} 0%, rgba(255,255,255,0.05) 100%)`,
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                      fontFamily: "'Playfair Display', Georgia, serif",
                    }}
                  >
                    {s.n}
                  </div>
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                    style={{
                      background: `${s.color}1f`,
                      color: s.color,
                      border: `1px solid ${s.color}40`,
                    }}
                  >
                    <Clock size={10} /> {s.time}
                  </span>
                </div>
                <div className="relative flex items-center gap-3 mb-2">
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: `${s.color}1f`,
                      boxShadow: `inset 0 0 0 1px ${s.color}55`,
                    }}
                  >
                    <Icon size={16} style={{ color: s.color }} />
                  </div>
                  <h3 className="text-white font-bold text-lg sm:text-xl tracking-tight leading-tight">
                    {s.t}
                  </h3>
                </div>
                <p className="relative text-white/60 text-sm leading-relaxed">
                  {s.d}
                </p>
                {/* Connector arrow on desktop */}
                {i < NMG_STEPS.length - 1 && (
                  <div
                    aria-hidden
                    className="hidden lg:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full items-center justify-center"
                    style={{
                      background: "#0a111c",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <ChevronRight size={14} className="text-white/35" />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {/* PAYMENT METHODS STRIP */}
      <section
        className="relative rounded-2xl border border-white/[0.07] p-5 sm:p-7 mb-6 sm:mb-8 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(6,182,212,0.04) 100%), #0a0f1a",
        }}
      >
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-5 lg:gap-8">
          <div className="lg:w-1/3">
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#22c55e] mb-1">
              ✦ Local payments
            </div>
            <h2 className="text-white text-xl sm:text-2xl font-black tracking-tight">
              Deposit instantly
            </h2>
            <p className="text-white/55 text-sm mt-1.5 leading-relaxed">
              Five trusted Bangladesh-friendly payment methods.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 w-full">
            {NMG_PAYMENTS.map((p) => (
              <div
                key={p.name}
                className="relative rounded-xl border border-white/[0.07] py-3.5 px-2 text-center hover:border-white/20 transition-colors"
                style={{ background: "#0d1421" }}
              >
                <div
                  className="h-7 w-7 mx-auto mb-2 rounded-md flex items-center justify-center"
                  style={{
                    background: `${p.color}22`,
                    boxShadow: `inset 0 0 0 1px ${p.color}55`,
                  }}
                >
                  <Zap size={12} style={{ color: p.color }} />
                </div>
                <div className="text-white text-xs sm:text-sm font-bold">
                  {p.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-6 sm:mb-8">
        <div className="mb-4 sm:mb-5">
          <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#06b6d4] mb-1">
            ✦ Frequently asked
          </div>
          <h2 className="text-white text-2xl sm:text-3xl font-black tracking-tight">
            Quick answers
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {NMG_FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-2xl border border-white/[0.07] p-4 sm:p-5 cursor-pointer hover:border-white/20 transition-colors open:border-[#22c55e]/40"
              style={{ background: "#0d1421" }}
            >
              <summary className="flex items-start gap-3 list-none">
                <div
                  className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    background: "rgba(34,197,94,0.12)",
                    boxShadow: "inset 0 0 0 1px rgba(34,197,94,0.4)",
                  }}
                >
                  <span className="text-[#22c55e] text-xs font-black">?</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-sm sm:text-base leading-snug">
                    {f.q}
                  </h3>
                </div>
                <ChevronRight
                  size={16}
                  className="text-white/35 group-open:rotate-90 transition-transform shrink-0"
                />
              </summary>
              <p className="text-white/65 text-sm mt-3 pl-10 sm:pl-11 leading-relaxed">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* SUPPORT CTA */}
      <section
        className="relative rounded-2xl sm:rounded-3xl border border-white/[0.07] p-5 sm:p-7 overflow-hidden"
        style={{
          background:
            "radial-gradient(120% 100% at 100% 0%, rgba(34,197,94,0.14), transparent 55%), linear-gradient(135deg, #0d1421 0%, #07101c 100%)",
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.28em] font-black text-[#22c55e] mb-2">
              ✦ Need a hand?
            </div>
            <h3 className="text-white text-lg sm:text-2xl font-black leading-tight mb-1.5">
              24/7 live chat in English & Bangla
            </h3>
            <p className="text-white/60 text-sm max-w-xl">
              Average response time under a minute. Tap the green headphones icon
              on any page to start a chat.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="guide-support-cta"
            className="inline-flex items-center gap-2 px-5 sm:px-6 h-11 sm:h-12 rounded-full font-bold text-sm shrink-0 transition-all hover:scale-[1.03]"
            style={{
              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
              color: "#fff",
              boxShadow:
                "0 10px 28px rgba(34,197,94,0.35), inset 0 1px 0 rgba(255,255,255,0.2)",
            }}
          >
            Contact support <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  </SiteLayout>
);
